import * as React from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { parseISO, addDays, startOfDay, isAfter } from "date-fns";
import {
    Button,
    DatePicker,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    FormCheckbox,
    Input,
    RadioInput,
    Select,
    TextArea,
} from "@jhool-io/fe-components";

import {
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import { AppointmentLocation } from "../../../../utils/types/session";
import {
    ClientEducation,
    ClientGender,
    ClientRace,
    ClientRelationship,
    ClientReligion,
    ClientSexualOrientation,
} from "../../../../utils/types/client";
import Accordion from "../../../Accordion/Accordion";
import NoteFormInfoBox from "../../NoteFormInfoBox/NoteFormInfoBox";
import { useFetchClientNotesList } from "../../../../hooks/queries/note";
import LoadPreviousSessionNotificationModal from "../../LoadPreviousSessionNotificatiModal/LoadPreviousSessionNotificationModal";

import {
    FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_FAMILY_NOTE_DETAILS,
    FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS,
} from "../../../../utils/constants";
import {
    emptyFamilyInitialDiagnosticEvaluationNote,
    FamilyInitialDiagnosticEvaluationNotePayload,
    IEditFamilyInitialDiagnosticEvaluationPayload,
    IGetClientNotes,
    ISessionNote,
} from "../../../../modules/notes/types/notes.types";
import {
    useEditNote,
    useNoteAutosave,
    useNoteValidationFeedback,
} from "../../../../modules/notes/hooks/edit-note";
import EditNoteFooter from "../../../../modules/notes/components/NoteCreationFooters/EditNoteFooter/EditNoteFooter";
import DiagnosisCodeSelect from "../../../../modules/notes/components/DiagnosisCodeSelect/DiagnosisCodeSelect";

interface EditFamilyInitialDiagnosticEvaluationProps {
    noteDetails: ISessionNote;
}

type Option = {
    label: React.ReactNode;
    value: string;
};

export default function EditFamilyInitialDiagnosticEvaluation({
    noteDetails,
}: EditFamilyInitialDiagnosticEvaluationProps) {
    const [localDateOfNextSession, setLocalDateOfNextSession] =
        React.useState<Date | null>(
            noteDetails?.note_content?.date_of_next_session
                ? parseISO(noteDetails?.note_content?.date_of_next_session)
                : null
        );

    const isSigning = React.useRef(false);

    const clientNoteParams = {
        type: noteDetails?.type as string,
        load_previous_notes: true,
        provider_id: noteDetails?.provider?.provider_id as string,
    };

    const { handleAutoSaveNote, handleEditDraftNote, handleSignNote } =
        useEditNote();

    const { data: notes } = useFetchClientNotesList(
        noteDetails?.client?.client_id as string,
        clientNoteParams
    );
    const [showPrompt, setShowPrompt] = React.useState(false);
    const [selectedNote, setSelectedNote] = React.useState(
        {} as IGetClientNotes
    );

    const {
        register,
        handleSubmit,
        getValues,
        control,
        setValue,
        watch,
        trigger,
        formState: { errors, submitCount },
    } = useForm<IEditFamilyInitialDiagnosticEvaluationPayload>({
        resolver: yupResolver(
            yup.object({
                family: yup.array().of(
                    yup.object().shape({
                        name: yup.string().when([], {
                            is: () => isSigning.current,
                            then: yup
                                .string()
                                .trim()
                                .required("Name is required"),
                            otherwise: yup.string(),
                        }),
                        preferred_name: yup.string(),
                        gender: yup.string().when([], {
                            is: () => isSigning.current,
                            then: yup.string().required("Gender is required"),
                            otherwise: yup.string(),
                        }),
                        other_gender: yup
                            .string()
                            .when("gender", (gender: string, field) =>
                                gender === "other" && isSigning.current
                                    ? field.trim().required("Field is required")
                                    : field
                            ),
                        pronouns: yup.string(),
                        religion: yup.string(),
                        education: yup.string(),
                        race: yup.string(),
                        ethinicity: yup.string(),
                        sexual_orientation: yup.string(),
                        other_sexual_orientation: yup
                            .string()
                            .when(
                                "sexual_orientation",
                                (sexual_orientation: string, field) =>
                                    sexual_orientation === "other" &&
                                    isSigning.current
                                        ? field
                                              .trim()
                                              .required("Field is required")
                                        : field
                            ),
                        current_employment: yup.string(),
                        current_employment_details: yup.string(),
                        relationship_status: yup.string(),
                        name_of_partner: yup.string(),
                        emergency_contact_information: yup.string(),
                        legal_problems: yup.string(),
                        anxiety_frequency: yup.string(),
                        anxiety_details: yup
                            .string()
                            .when("anxiety", (anxiety: boolean, field) =>
                                anxiety && isSigning.current
                                    ? field
                                          .trim()
                                          .required(
                                              "Anxiety details is required"
                                          )
                                          .min(
                                              20,
                                              "Anxiety details must contain at least 20 characters"
                                          )
                                    : field
                            ),
                        panic_attacks_freqency: yup.string(),
                        panic_attacks_details: yup
                            .string()
                            .when(
                                "panic_attacks",
                                (panic_attacks: boolean, field) =>
                                    panic_attacks && isSigning.current
                                        ? field
                                              .trim()
                                              .required(
                                                  "Panic attacks details is required"
                                              )
                                              .min(
                                                  20,
                                                  "Panic attacks details must contain at least 20 characters"
                                              )
                                        : field
                            ),
                        depression_freqency: yup.string(),
                        depression_details: yup
                            .string()
                            .when("depression", (depression: boolean, field) =>
                                depression && isSigning.current
                                    ? field
                                          .trim()
                                          .required(
                                              "Depression details is required"
                                          )
                                          .min(
                                              20,
                                              "Depression details must contain at least 20 characters"
                                          )
                                    : field
                            ),
                        selected_symptoms: yup.array().nullable(),
                        other_symptoms: yup.string(),
                        physical_symptoms: yup.string(),
                        medication_physical_psychiatric: yup.string(),
                        sleeping_habits_and_concerns: yup.string(),
                        difficulties_with_appetite_or_eating_patterns:
                            yup.string(),
                        parents_stepparents_history: yup.string(),
                        siblings_history: yup.string(),
                        grandparents_history: yup.string(),
                        children_history: yup.string(),
                        other_family_members_history: yup.string(),
                        family_members_alcohol_abuse: yup.array().nullable(),
                        family_members_anxiety: yup.array().nullable(),
                        family_members_bipolar_disorder: yup.array().nullable(),
                        family_members_depression: yup.array().nullable(),
                        family_members_ptsd: yup.array().nullable(),
                        family_members_schizophrenia: yup.array().nullable(),
                        family_members_substance_abuse: yup.array().nullable(),
                        family_members_substance_abuse_substance: yup.string(),
                        trauma_abuse_history: yup.string(),
                        previous_mental_health_treatment_out_patient:
                            yup.string(),
                        impatient_psychiatric_hospitalization: yup.string(),
                        relevant_medical_history: yup.string(),
                        been_so_distressed_you_seriously_wished_to_end_your_life:
                            yup.string().when([], {
                                is: () => isSigning.current,
                                then: yup
                                    .string()
                                    .required("This field is required"),
                                otherwise: yup.string().nullable(),
                            }),
                        describe_been_so_distressed_you_seriously_wished_to_end_your_life:
                            yup
                                .string()
                                .when(
                                    "been_so_distressed_you_seriously_wished_to_end_your_life",
                                    (
                                        been_so_distressed_you_seriously_wished_to_end_your_life,
                                        field
                                    ) =>
                                        (been_so_distressed_you_seriously_wished_to_end_your_life ===
                                            "Today" ||
                                            been_so_distressed_you_seriously_wished_to_end_your_life ===
                                                "Recently") &&
                                        isSigning.current
                                            ? field
                                                  .trim()
                                                  .required(
                                                      "Description field is required"
                                                  )
                                                  .min(
                                                      20,
                                                      "Description field must contain at least 20 characters"
                                                  )
                                            : field
                                ),
                        has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live:
                            yup.string().when([], {
                                is: () => isSigning.current,
                                then: yup
                                    .string()
                                    .required("This field is required"),
                                otherwise: yup.string().nullable(),
                            }),
                        describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live:
                            yup
                                .string()
                                .when(
                                    "has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live",
                                    (
                                        has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live,
                                        field
                                    ) =>
                                        has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live ===
                                            "Yes" && isSigning.current
                                            ? field
                                                  .trim()
                                                  .required(
                                                      "Description field is required"
                                                  )
                                                  .min(
                                                      20,
                                                      "Description field must contain at least 20 characters"
                                                  )
                                            : field
                                ),
                        a_specific_plan_of_how_you_would_kill_yourself: yup
                            .string()
                            .when([], {
                                is: () => isSigning.current,
                                then: yup
                                    .string()
                                    .required("This field is required"),
                                otherwise: yup.string().nullable(),
                            }),
                        describe_a_specific_plan_of_how_you_would_kill_yourself:
                            yup
                                .string()
                                .when(
                                    "a_specific_plan_of_how_you_would_kill_yourself",
                                    (
                                        a_specific_plan_of_how_you_would_kill_yourself,
                                        field
                                    ) =>
                                        a_specific_plan_of_how_you_would_kill_yourself ===
                                            "Yes" && isSigning.current
                                            ? field
                                                  .trim()
                                                  .required(
                                                      "Description field is required"
                                                  )
                                                  .min(
                                                      20,
                                                      "Description field must contain at least 20 characters"
                                                  )
                                            : field
                                ),
                        access_to_weapons_or_means_of_hurting_self: yup
                            .string()
                            .when([], {
                                is: () => isSigning.current,
                                then: yup
                                    .string()
                                    .required("This field is required"),
                                otherwise: yup.string().nullable(),
                            }),
                        describe_access_to_weapons_or_means_of_hurting_self: yup
                            .string()
                            .when(
                                "access_to_weapons_or_means_of_hurting_self",
                                (
                                    access_to_weapons_or_means_of_hurting_self,
                                    field
                                ) =>
                                    access_to_weapons_or_means_of_hurting_self ===
                                        "Yes" && isSigning.current
                                        ? field
                                              .trim()
                                              .required(
                                                  "Description field is required"
                                              )
                                              .min(
                                                  20,
                                                  "Description field must contain at least 20 characters"
                                              )
                                        : field
                            ),
                        made_serious_suicide_attempts: yup.string().when([], {
                            is: () => isSigning.current,
                            then: yup
                                .string()
                                .required("This field is required"),
                            otherwise: yup.string().nullable(),
                        }),
                        describe_made_serious_suicide_attempts: yup
                            .string()
                            .when(
                                "made_serious_suicide_attempts",
                                (made_serious_suicide_attempts, field) =>
                                    made_serious_suicide_attempts === "Yes" &&
                                    isSigning.current
                                        ? field
                                              .trim()
                                              .required(
                                                  "Description field is required"
                                              )
                                              .min(
                                                  20,
                                                  "Description field must contain at least 20 characters"
                                              )
                                        : field
                            ),
                        purposely_done_something_to_hurt_yourself: yup
                            .string()
                            .when([], {
                                is: () => isSigning.current,
                                then: yup
                                    .string()
                                    .required("This field is required"),
                                otherwise: yup.string().nullable(),
                            }),
                        describe_done_something_to_hurt_yourself: yup
                            .string()
                            .when(
                                "purposely_done_something_to_hurt_yourself",
                                (
                                    purposely_done_something_to_hurt_yourself,
                                    field
                                ) =>
                                    purposely_done_something_to_hurt_yourself ===
                                        "Yes" && isSigning.current
                                        ? field
                                              .trim()
                                              .required(
                                                  "Description field is required"
                                              )
                                              .min(
                                                  20,
                                                  "Description field must contain at least 20 characters"
                                              )
                                        : field
                            ),
                        heard_voices_telling_you_to_hurt_yourself: yup
                            .string()
                            .when([], {
                                is: () => isSigning.current,
                                then: yup
                                    .string()
                                    .required("This field is required"),
                                otherwise: yup.string().nullable(),
                            }),
                        describe_heard_voices_telling_you_to_hurt_yourself: yup
                            .string()
                            .when(
                                "heard_voices_telling_you_to_hurt_yourself",
                                (
                                    heard_voices_telling_you_to_hurt_yourself,
                                    field
                                ) =>
                                    heard_voices_telling_you_to_hurt_yourself ===
                                        "Yes" && isSigning.current
                                        ? field
                                              .trim()
                                              .required(
                                                  "Description field is required"
                                              )
                                              .min(
                                                  20,
                                                  "Description field must contain at least 20 characters"
                                              )
                                        : field
                            ),
                        had_relatives_who_attempted_or_commited_sucide: yup
                            .string()
                            .when([], {
                                is: () => isSigning.current,
                                then: yup
                                    .string()
                                    .required("This field is required"),
                                otherwise: yup.string().nullable(),
                            }),
                        describe_had_relatives_who_attempted_or_commited_sucide:
                            yup
                                .string()
                                .when(
                                    "had_relatives_who_attempted_or_commited_sucide",
                                    (
                                        had_relatives_who_attempted_or_commited_sucide,
                                        field
                                    ) =>
                                        had_relatives_who_attempted_or_commited_sucide ===
                                            "Yes" && isSigning.current
                                            ? field
                                                  .trim()
                                                  .required(
                                                      "Description field is required"
                                                  )
                                                  .min(
                                                      20,
                                                      "Description field must contain at least 20 characters"
                                                  )
                                            : field
                                ),
                        had_thoughts_of_killing_or_seriously_hurting_someone:
                            yup.string().when([], {
                                is: () => isSigning.current,
                                then: yup
                                    .string()
                                    .required("This field is required"),
                                otherwise: yup.string().nullable(),
                            }),
                        describe_had_thoughts_of_killing_or_seriously_hurting_someone:
                            yup
                                .string()
                                .when(
                                    "had_thoughts_of_killing_or_seriously_hurting_someone",
                                    (
                                        had_thoughts_of_killing_or_seriously_hurting_someone,
                                        field
                                    ) =>
                                        had_thoughts_of_killing_or_seriously_hurting_someone ===
                                            "Yes" && isSigning.current
                                            ? field
                                                  .trim()
                                                  .required(
                                                      "Description field is required"
                                                  )
                                                  .min(
                                                      20,
                                                      "Description field must contain at least 20 characters"
                                                  )
                                            : field
                                ),
                        heard_voices_telling_you_to_hurt_others: yup
                            .string()
                            .when([], {
                                is: () => isSigning.current,
                                then: yup
                                    .string()
                                    .required("This field is required"),
                                otherwise: yup.string().nullable(),
                            }),
                        describe_heard_voices_telling_you_to_hurt_others: yup
                            .string()
                            .when(
                                "heard_voices_telling_you_to_hurt_others",
                                (
                                    heard_voices_telling_you_to_hurt_others,
                                    field
                                ) =>
                                    heard_voices_telling_you_to_hurt_others ===
                                        "Yes" && isSigning.current
                                        ? field
                                              .trim()
                                              .required(
                                                  "Description field is required"
                                              )
                                              .min(
                                                  20,
                                                  "Description field must contain at least 20 characters"
                                              )
                                        : field
                            ),
                        hurt_someone_or_destroyed_property_on_purpose: yup
                            .string()
                            .when([], {
                                is: () => isSigning.current,
                                then: yup
                                    .string()
                                    .required("This field is required"),
                                otherwise: yup.string().nullable(),
                            }),
                        describe_hurt_someone_or_destroyed_property_on_purpose:
                            yup
                                .string()
                                .when(
                                    "hurt_someone_or_destroyed_property_on_purpose",
                                    (
                                        hurt_someone_or_destroyed_property_on_purpose,
                                        field
                                    ) =>
                                        hurt_someone_or_destroyed_property_on_purpose ===
                                            "Yes" && isSigning.current
                                            ? field
                                                  .trim()
                                                  .required(
                                                      "Description field is required"
                                                  )
                                                  .min(
                                                      20,
                                                      "Description field must contain at least 20 characters"
                                                  )
                                            : field
                                ),
                        slapped_kicked_punched_someone_with_intent_to_harm: yup
                            .string()
                            .when([], {
                                is: () => isSigning.current,
                                then: yup
                                    .string()
                                    .required("This field is required"),
                                otherwise: yup.string().nullable(),
                            }),
                        describe_slapped_kicked_punched_someone_with_intent_to_harm:
                            yup
                                .string()
                                .when(
                                    "slapped_kicked_punched_someone_with_intent_to_harm",
                                    (
                                        slapped_kicked_punched_someone_with_intent_to_harm,
                                        field
                                    ) =>
                                        slapped_kicked_punched_someone_with_intent_to_harm ===
                                            "Yes" && isSigning.current
                                            ? field
                                                  .trim()
                                                  .required(
                                                      "Description field is required"
                                                  )
                                                  .min(
                                                      20,
                                                      "Description field must contain at least 20 characters"
                                                  )
                                            : field
                                ),
                        been_arrested_or_detained_for_violent_behavior: yup
                            .string()
                            .when([], {
                                is: () => isSigning.current,
                                then: yup
                                    .string()
                                    .required("This field is required"),
                                otherwise: yup.string().nullable(),
                            }),
                        describe_been_arrested_or_detained_for_violent_behavior:
                            yup
                                .string()
                                .when(
                                    "been_arrested_or_detained_for_violent_behavior",
                                    (
                                        been_arrested_or_detained_for_violent_behavior,
                                        field
                                    ) =>
                                        been_arrested_or_detained_for_violent_behavior ===
                                            "Yes" && isSigning.current
                                            ? field
                                                  .trim()
                                                  .required(
                                                      "Description field is required"
                                                  )
                                                  .min(
                                                      20,
                                                      "Description field must contain at least 20 characters"
                                                  )
                                            : field
                                ),
                        been_to_jail_for_any_reason: yup.string().when([], {
                            is: () => isSigning.current,
                            then: yup
                                .string()
                                .required("This field is required"),
                            otherwise: yup.string().nullable(),
                        }),
                        describe_been_to_jail_for_any_reason: yup
                            .string()
                            .when(
                                "been_to_jail_for_any_reason",
                                (been_to_jail_for_any_reason, field) =>
                                    been_to_jail_for_any_reason === "Yes" &&
                                    isSigning.current
                                        ? field
                                              .trim()
                                              .required(
                                                  "Description field is required"
                                              )
                                              .min(
                                                  20,
                                                  "Description field must contain at least 20 characters"
                                              )
                                        : field
                            ),
                        been_on_probation_for_any_reason: yup
                            .string()
                            .when([], {
                                is: () => isSigning.current,
                                then: yup
                                    .string()
                                    .required("This field is required"),
                                otherwise: yup.string().nullable(),
                            }),
                        describe_been_on_probation_for_any_reason: yup
                            .string()
                            .when(
                                "been_on_probation_for_any_reason",
                                (been_on_probation_for_any_reason, field) =>
                                    been_on_probation_for_any_reason ===
                                        "Yes" && isSigning.current
                                        ? field
                                              .trim()
                                              .required(
                                                  "Description field is required"
                                              )
                                              .min(
                                                  20,
                                                  "Description field must contain at least 20 characters"
                                              )
                                        : field
                            ),
                        do_you_have_access_to_guns: yup.string().when([], {
                            is: () => isSigning.current,
                            then: yup
                                .string()
                                .required("This field is required"),
                            otherwise: yup.string().nullable(),
                        }),
                        describe_do_you_have_access_to_guns: yup
                            .string()
                            .when(
                                "do_you_have_access_to_guns",
                                (do_you_have_access_to_guns, field) =>
                                    do_you_have_access_to_guns === "Yes" &&
                                    isSigning.current
                                        ? field
                                              .trim()
                                              .required(
                                                  "Description field is required"
                                              )
                                              .min(
                                                  20,
                                                  "Description field must contain at least 20 characters"
                                              )
                                        : field
                            ),
                        mental_health_appearance: yup.array().when([], {
                            is: () => isSigning.current,
                            then: yup
                                .array()
                                .min(1)
                                .required("Please select at least one option"),
                            otherwise: yup.array().nullable(),
                        }),
                        mental_health_movement: yup.array().when([], {
                            is: () => isSigning.current,
                            then: yup
                                .array()
                                .min(1)
                                .required("Please select at least one option"),
                            otherwise: yup.array().nullable(),
                        }),
                        mental_health_speech_behaviour: yup.array().when([], {
                            is: () => isSigning.current,
                            then: yup
                                .array()
                                .min(1)
                                .required("Please select at least one option"),
                            otherwise: yup.array().nullable(),
                        }),
                        mental_health_affect_mood: yup.array().when([], {
                            is: () => isSigning.current,
                            then: yup
                                .array()
                                .min(1)
                                .required("Please select at least one option"),
                            otherwise: yup.array().nullable(),
                        }),
                        mental_health_attention_concentration_effort: yup
                            .array()
                            .when([], {
                                is: () => isSigning.current,
                                then: yup
                                    .array()
                                    .min(1)
                                    .required(
                                        "Please select at least one option"
                                    ),
                                otherwise: yup.array().nullable(),
                            }),
                        mental_health_presentation_of_self: yup
                            .array()
                            .when([], {
                                is: () => isSigning.current,
                                then: yup
                                    .array()
                                    .min(1)
                                    .required(
                                        "Please select at least one option"
                                    ),
                                otherwise: yup.array().nullable(),
                            }),
                        mental_health_cognition: yup.array().when([], {
                            is: () => isSigning.current,
                            then: yup
                                .array()
                                .min(1)
                                .required("Please select at least one option"),
                            otherwise: yup.array().nullable(),
                        }),
                        patients_ability_and_capability_to_respond_to_treatment:
                            yup.array().when([], {
                                is: () => isSigning.current,
                                then: yup
                                    .array()
                                    .min(1)
                                    .required("This field is required"),
                                otherwise: yup.array().nullable(),
                            }),
                        details_for_diminished_poor_limited_quality_patient_oriented_evidence:
                            yup
                                .string()
                                .when(
                                    "patients_ability_and_capability_to_respond_to_treatment",
                                    (
                                        patients_ability_and_capability_to_respond_to_treatment: string[],
                                        field
                                    ) =>
                                        patients_ability_and_capability_to_respond_to_treatment &&
                                        patients_ability_and_capability_to_respond_to_treatment.includes(
                                            "Diminished/poor or limited-quality patient-oriented evidence"
                                        )
                                            ? field.required(
                                                  "Details is required"
                                              )
                                            : field
                                ),
                        anxiety: yup.bool(),
                        panic_attacks: yup.bool(),
                        depression: yup.bool(),
                    })
                ),
                duration: yup.string(),
                isClientPresent: yup.bool(),
                appointment_location: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("Session location is required"),
                    otherwise: yup.string(),
                }),
                delivery_option: yup
                    .string()
                    .nullable()
                    .when(
                        "appointment_location",
                        (appointment_location, field) =>
                            appointment_location === "teletherapy" &&
                            isSigning.current
                                ? field.required(
                                      "Client teletherapy location is required"
                                  )
                                : field
                    ),
                provider_location: yup
                    .string()
                    .nullable()
                    .when(
                        "appointment_location",
                        (appointment_location, field) =>
                            appointment_location === "teletherapy" &&
                            isSigning.current
                                ? field.required(
                                      "Provider teletherapy location is required"
                                  )
                                : field
                    ),
                other_delivery_option: yup
                    .string()
                    .when("delivery_option", (delivery_option, field) =>
                        delivery_option === "Other" && isSigning.current
                            ? field
                                  .trim()
                                  .required(
                                      "Other client teletherapy location is required"
                                  )
                            : field
                    ),
                other_provider_location: yup
                    .string()
                    .when("provider_location", (provider_location, field) =>
                        provider_location === "Other" && isSigning.current
                            ? field
                                  .trim()
                                  .required(
                                      "Other provider teletherapy location is required"
                                  )
                            : field
                    ),
                consent_was_received_for_the_teletherapy_session: yup.bool(),
                the_teletherapy_session_was_conducted_via_video: yup.bool(),
                diagnosis_codes: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .max(1, "You can only select 1 diagnosis")
                        .min(1, "Diagnosis is required")
                        .required("Diagnosis is required"),
                    otherwise: yup
                        .array()
                        .nullable()
                        .max(1, "You can only select 1 diagnosis"),
                }),
                secondary_diagnosis: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                tertiary_diagnosis: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                additional_diagnosis_one: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                additional_diagnosis_two: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                what_are_the_problem_you_are_seeking_help_for: yup
                    .string()
                    .when([], {
                        is: () => isSigning.current,
                        then: yup
                            .string()
                            .trim()
                            .required(
                                "What is the problem you are seeking help for is required"
                            )
                            .min(
                                20,
                                "What is the problem you are seeking help for must contain at least 20 characters"
                            ),
                        otherwise: yup.string(),
                    }),
                people_present_at_session: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .min(1)
                        .required("People present at session is required"),
                    otherwise: yup.array().nullable(),
                }),
                other_person_present_at_session: yup.string(),
                type_of_treatment_recommended: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .min(1)
                        .required("Type of treatment recommended is required"),
                    otherwise: yup.array().nullable(),
                }),
                treatment_modality_intervention: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .min(1)
                        .required(
                            "Treatment modality intervention is required"
                        ),
                    otherwise: yup.array().nullable(),
                }),
                other_treatment_modality_intervention: yup.string(),
                treatment_goal_one: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("Treatment goal one is required"),
                    otherwise: yup.string(),
                }),
                treatment_goal_two: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("Treatment goal two is required"),
                    otherwise: yup.string(),
                }),
                treatment_goal_three: yup.string(),
                frequency_of_sessions: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("This field is required"),
                    otherwise: yup.string().nullable(),
                }),
                other_frequency_of_sessions: yup
                    .string()
                    .when(
                        "frequency_of_sessions",
                        (frequency_of_sessions, field) =>
                            frequency_of_sessions === "Other" &&
                            isSigning.current
                                ? field
                                      .trim()
                                      .required(
                                          "Other frequency of sessions is required"
                                      )
                                : field
                    ),
                date_of_next_session: yup.date().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .date()
                        .required("Date of next session is required")
                        .test(
                            "is-after-dos",
                            "Date of next session must be after the current session date",
                            function validateDateAfterDOS(value) {
                                if (!value) return false;
                                if (!noteDetails.date_of_service) return true;
                                const dos = startOfDay(
                                    parseISO(noteDetails.date_of_service)
                                );
                                const nextSession = startOfDay(value);
                                return isAfter(nextSession, dos);
                            }
                        ),
                    otherwise: yup.date().nullable(),
                }),
                subjective_report: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("Subjective report is required")
                        .min(
                            20,
                            "Subjective report must contain at least 20 characters"
                        ),
                    otherwise: yup.string(),
                }),
                summary: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("Summary is required")
                        .min(20, "Summary must contain at least 20 characters"),
                    otherwise: yup.string(),
                }),
                prognosis: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("Prognosis is required")
                        .min(
                            20,
                            "Prognosis must contain at least 20 characters"
                        ),
                    otherwise: yup.string(),
                }),
                supervisory_intake_statement: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .min(1)
                        .required("Supervisory intake statement is required"),
                    otherwise: yup.array().nullable(),
                }),
                medical_necessity_rationale: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .min(1)
                        .required("Medical necessity rationale is required"),
                    otherwise: yup.array().nullable(),
                }),
                risk_review_supervision_plan: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .min(1)
                        .required("Risk review supervision plan is required"),
                    otherwise: yup.array().nullable(),
                }),
                formal_safety_plan_details: yup.string(),
                higher_level_of_care_details: yup.string(),
                signature: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().trim().required("Signature is required"),
                    otherwise: yup.string(),
                }),
            })
        ),
        mode: "onChange",
        defaultValues: noteDetails.note_content
            ? {
                  ...noteDetails.note_content,
                  secondary_diagnosis: noteDetails?.note_content
                      ?.secondary_diagnosis
                      ? [noteDetails?.note_content?.secondary_diagnosis]
                      : undefined,
                  tertiary_diagnosis: noteDetails?.note_content
                      ?.tertiary_diagnosis
                      ? [noteDetails?.note_content?.tertiary_diagnosis]
                      : undefined,
                  additional_diagnosis_one: noteDetails?.note_content
                      ?.additional_diagnosis_one
                      ? [noteDetails?.note_content?.additional_diagnosis_one]
                      : undefined,
                  additional_diagnosis_two: noteDetails?.note_content
                      ?.additional_diagnosis_two
                      ? [noteDetails?.note_content?.additional_diagnosis_two]
                      : undefined,
                  supervisory_intake_statement:
                      noteDetails?.note_content?.supervisory_intake_statement ??
                      null,
                  medical_necessity_rationale:
                      noteDetails?.note_content?.medical_necessity_rationale ??
                      null,
                  family:
                      noteDetails?.note_content?.family?.length > 0
                          ? noteDetails.note_content.family
                          : [
                                {
                                    ...emptyFamilyInitialDiagnosticEvaluationNote,
                                    name: `${noteDetails.client.first_name} ${noteDetails.client.last_name}`,
                                },
                            ],
              }
            : undefined,
        shouldFocusError: false,
    });

    const watchedValues = watch();

    const supervisionPlan = watch("risk_review_supervision_plan");
    const shouldShowFormalSafetyPlan =
        Array.isArray(supervisionPlan) &&
        supervisionPlan.includes("Formal safety plan completed/updated");
    const shouldShowHigherLevelOfCare =
        Array.isArray(supervisionPlan) &&
        supervisionPlan.includes("Higher level of care considered/recommended");

    // Function to handle item click and set the selected note content
    const handleLoadPreviousSession = (note: IGetClientNotes) => {
        setValue("isClientPresent", note.note_content.isClientPresent);
        setValue(
            "appointment_location",
            note?.note_content.appointment_location
        );
        setValue(
            "consent_was_received_for_the_teletherapy_session",
            note?.note_content.consent_was_received_for_the_teletherapy_session
        );
        setValue("delivery_option", note.note_content.delivery_option);
        setValue("diagnosis_codes", note.note_content.diagnosis_codes);
        setValue("secondary_diagnosis", [
            note.note_content.secondary_diagnosis,
        ]);
        setValue("tertiary_diagnosis", [note.note_content.tertiary_diagnosis]);
        setValue("additional_diagnosis_one", [
            note.note_content.additional_diagnosis_one,
        ]);
        setValue("additional_diagnosis_two", [
            note.note_content.additional_diagnosis_two,
        ]);
        setValue("duration", note.note_content.duration);
        setValue("family", note.note_content.family);
        setValue(
            "family.0.name",
            `${noteDetails.client.first_name} ${noteDetails.client.last_name}` ||
                ""
        );
        setValue(
            "other_delivery_option",
            note.note_content.other_delivery_option
        );
        setValue(
            "other_person_present_at_session",
            note.note_content.other_person_present_at_session
        );
        setValue(
            "other_provider_location",
            note.note_content.other_provider_location
        );
        setValue(
            "other_treatment_modality_intervention",
            note.note_content.other_treatment_modality_intervention
        );
        setValue(
            "people_present_at_session",
            note.note_content.people_present_at_session
        );
        setValue("prognosis", note.note_content.prognosis);
        setValue("provider_location", note.note_content.provider_location);
        setValue("subjective_report", note.note_content.subjective_report);
        setValue("summary", note.note_content.summary);
        setValue(
            "the_teletherapy_session_was_conducted_via_video",
            note.note_content.the_teletherapy_session_was_conducted_via_video
        );
        setValue("treatment_goal_one", note.note_content.treatment_goal_one);
        setValue(
            "treatment_goal_three",
            note.note_content.treatment_goal_three
        );
        setValue(
            "frequency_of_sessions",
            note.note_content.frequency_of_sessions
        );
        setValue(
            "other_frequency_of_sessions",
            note.note_content.other_frequency_of_sessions
        );
        setValue("treatment_goal_two", note.note_content.treatment_goal_two);
        setValue(
            "treatment_modality_intervention",
            note.note_content.treatment_modality_intervention
        );
        setValue(
            "type_of_treatment_recommended",
            note.note_content.type_of_treatment_recommended
        );
        setValue(
            "what_are_the_problem_you_are_seeking_help_for",
            note.note_content.what_are_the_problem_you_are_seeking_help_for
        );
        setValue(
            "supervisory_intake_statement",
            note.note_content.supervisory_intake_statement
        );
        setValue(
            "medical_necessity_rationale",
            note.note_content.medical_necessity_rationale
        );
        setValue("risk_review_supervision_plan", [
            note.note_content.risk_review_supervision_plan,
        ]);
        setValue(
            "formal_safety_plan_details",
            note.note_content.formal_safety_plan_details
        );
        setValue(
            "higher_level_of_care_details",
            note.note_content.higher_level_of_care_details
        );
        setShowPrompt(false);
    };

    const { fields, append, remove } = useFieldArray({
        control,
        name: "family",
    });

    // Get frequency for select
    const FrequencyForSelect = ["daily", "weekly", "monthly", "yearly"].map(
        (item) => ({
            label: makeStringFirstLetterCapital(item),
            value: item,
        })
    );

    // Get relationship options for select
    const RelationshipForSelect = Object.values(ClientRelationship).map(
        (relationship) => ({
            label: removeEnumUnderscore(
                makeStringFirstLetterCapital(relationship)
            ),
            value: relationship,
        })
    );

    // Get gender options for select
    const GenderForSelect = Object.values(ClientGender).map((gender) => ({
        label: removeEnumUnderscore(makeStringFirstLetterCapital(gender)),
        value: gender,
    }));

    // Get religion options for select
    const ReligionForSelect = Object.values(ClientReligion).map((religion) => ({
        label: removeEnumUnderscore(makeStringFirstLetterCapital(religion)),
        value: religion,
    }));

    // Get education options for select
    const EducationForSelect = Object.values(ClientEducation).map(
        (education) => ({
            label: removeEnumUnderscore(
                makeStringFirstLetterCapital(education)
            ),
            value: education,
        })
    );

    // Get sexual orientation options for select
    const SexualOrientationForSelect = Object.values(
        ClientSexualOrientation
    ).map((orientation) => ({
        label: removeEnumUnderscore(makeStringFirstLetterCapital(orientation)),
        value: orientation,
    }));

    // Get education options for select
    const RaceForSelect = Object.values(ClientRace).map((race) => ({
        label: removeEnumUnderscore(makeStringFirstLetterCapital(race)),
        value: race,
    }));

    // Get appointment location options
    const AppointmentLocationsForSelect = Object.values(
        AppointmentLocation
    ).map((location) => {
        return {
            label: removeEnumUnderscore(makeStringFirstLetterCapital(location)),
            value: location,
        };
    });

    const isChecked = (
        checkboxValue: string,
        checkboxArray: string[] | null
    ) => {
        if (!checkboxArray) return false;
        return Boolean(
            checkboxArray.find((value: string) => value === checkboxValue)
        );
    };

    const isCheckboxChecked = (checkboxValue: string, key: string) => {
        return Boolean(
            noteDetails.note_content?.[key]?.find(
                (value: string) => value === checkboxValue
            )
        );
    };

    // Get client whose session this note is linked to
    const clientName = `${noteDetails.client.first_name} ${noteDetails.client.last_name}`;

    useNoteValidationFeedback({
        errors,
        submitCount,
        isSigningRef: isSigning,
        trigger,
    });

    useNoteAutosave({
        watchedValues,
        getValues,
        handleSubmit,
        onSave: handleAutoSaveNote,
        isSigningRef: isSigning,
    });

    return (
        <>
            <LoadPreviousSessionNotificationModal
                showPrompt={showPrompt}
                setShowPrompt={setShowPrompt}
                handleLoadPreviousSession={() =>
                    handleLoadPreviousSession(selectedNote)
                }
            />
            {notes && notes.data.length > 0 && (
                <div className="flex justify-end mb-[--spacing-header]">
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton>
                                Load previous session note
                            </FilterButton>
                        </DropdownTrigger>
                        <DropdownContent width="auto" align="end">
                            {notes &&
                                notes?.data?.map((note) => (
                                    <DropdownItem
                                        key={note.note_id}
                                        onClick={() => {
                                            setShowPrompt(true);
                                            setSelectedNote(note);
                                        }}
                                    >
                                        <div>
                                            {makeStringFirstLetterCapital(
                                                removeEnumUnderscore(
                                                    note.note_type
                                                )
                                            )}{" "}
                                            - Note content Details -{" "}
                                            {formatDate(note.date_of_service)}
                                        </div>
                                    </DropdownItem>
                                ))}
                        </DropdownContent>
                    </Dropdown>
                </div>
            )}
            <div>
                <div className="mb-4 flex items-center">
                    <p className="note-label !mb-0 !text-base">
                        Family Count -<span>{fields.length}</span>
                    </p>
                    <div className="ml-[--spacing-items] flex items-center gap-x-[--spacing-items]">
                        <Button
                            onClick={() =>
                                append(
                                    emptyFamilyInitialDiagnosticEvaluationNote,
                                    {
                                        shouldFocus: false,
                                    }
                                )
                            }
                        >
                            Add
                        </Button>
                        <Button
                            onClick={() => remove(fields.length - 1)}
                            disabled={fields.length === 1}
                            variant="secondary"
                        >
                            Remove
                        </Button>
                    </div>
                </div>
            </div>
            <h3 className="text-base font-medium mb-[--spacing-items]">
                Family Initial Diagnostic Evaluation Form
            </h3>
            <form
                aria-label="family initial diagnostic evaluation"
                id="edit-note-form"
                onSubmit={(event) => {
                    isSigning.current = true;
                    handleSubmit(handleSignNote)(event);
                }}
            >
                <div className="mb-[--spacing-header]">
                    <NoteFormInfoBox />
                </div>
                <div className="fg">
                    <FormCheckbox
                        {...register("isClientPresent")}
                        label={
                            FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.isClientPresent
                        }
                        defaultChecked={
                            noteDetails.note_content?.isClientPresent
                        }
                    />
                </div>
                <section id="step-one" className="mb-10">
                    <h3 className="note-label note-label-req">
                        {
                            FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.supervisory_intake_statement
                        }
                    </h3>
                    <div className="note-checkboxes">
                        <Controller
                            name="supervisory_intake_statement"
                            control={control}
                            render={({ field }) => {
                                const checkboxValue =
                                    "This diagnostic intake was completed by a licensed clinician, which helped determine diagnosis, medical necessity, and initial treatment recommendations";
                                return (
                                    <FormCheckbox
                                        label={checkboxValue}
                                        value={checkboxValue}
                                        checked={
                                            field.value?.includes(
                                                checkboxValue
                                            ) ?? false
                                        }
                                        onChange={(e) =>
                                            field.onChange(
                                                e.target.checked
                                                    ? [checkboxValue]
                                                    : null
                                            )
                                        }
                                    />
                                );
                            }}
                        />
                    </div>
                    {errors.supervisory_intake_statement ? (
                        <div className="note-error">
                            <p>Supervisory intake statement is required</p>
                        </div>
                    ) : null}
                </section>
                <div className="fg">
                    <Controller
                        name="appointment_location"
                        control={control}
                        render={({ field }) => (
                            <Select
                                label={
                                    FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.appointment_location
                                }
                                placeholder=""
                                onChange={(val) =>
                                    field.onChange((val as Option).value)
                                }
                                value={AppointmentLocationsForSelect.find(
                                    (location) =>
                                        location.value ===
                                            watch("appointment_location") ||
                                        null
                                )}
                                options={AppointmentLocationsForSelect}
                                isRequired
                                hasError={!!errors.appointment_location}
                                errorText={errors.appointment_location?.message}
                            />
                        )}
                    />
                </div>
                {watch(`appointment_location`) ===
                    AppointmentLocation.TELETHERAPY && (
                    <>
                        <p className="note-label">
                            {
                                FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.delivery_option
                            }
                        </p>
                        <div className="fg fg-space-between three flex">
                            <RadioInput
                                {...register(`delivery_option`)}
                                label="Home"
                                value="Home"
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.delivery_option === "Home"
                                }
                            />

                            <RadioInput
                                {...register(`delivery_option`)}
                                label="Office"
                                value="Office"
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.delivery_option === "Office"
                                }
                            />

                            <RadioInput
                                {...register(`delivery_option`)}
                                label="Other"
                                value="Other"
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.delivery_option === "Other"
                                }
                            />
                        </div>
                        {errors.delivery_option ? (
                            <div className="note-error">
                                <p>Delivery option is required</p>
                            </div>
                        ) : null}
                        {watch(`delivery_option`) === "Other" ||
                        noteDetails.note_content?.delivery_option ===
                            "Other" ? (
                            <div className="fg">
                                <Input
                                    {...register(`other_delivery_option`)}
                                    label={
                                        FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.other_delivery_option
                                    }
                                    hasError={!!errors?.other_delivery_option}
                                    errorText={
                                        errors?.other_delivery_option?.message
                                    }
                                    defaultValue={
                                        noteDetails.note_content
                                            ?.other_delivery_option
                                    }
                                />
                            </div>
                        ) : null}
                    </>
                )}
                {watch(`appointment_location`) ===
                    AppointmentLocation.TELETHERAPY && (
                    <>
                        <p className="note-label">
                            {
                                FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.provider_location
                            }
                        </p>
                        <div className="fg fg-space-between three flex">
                            <RadioInput
                                {...register(`provider_location`)}
                                label="Home"
                                value="Home"
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.provider_location === "Home"
                                }
                            />

                            <RadioInput
                                {...register(`provider_location`)}
                                label="Office"
                                value="Office"
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.provider_location === "Office"
                                }
                            />

                            <RadioInput
                                {...register(`provider_location`)}
                                label="Other"
                                value="Other"
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.provider_location === "Other"
                                }
                            />
                        </div>
                        {errors.provider_location ? (
                            <div className="note-error">
                                <p>Delivery option is required</p>
                            </div>
                        ) : null}
                        {watch(`provider_location`) === "Other" ||
                        noteDetails.note_content?.provider_location ===
                            "Other" ? (
                            <div className="fg">
                                <Input
                                    {...register(`other_provider_location`)}
                                    label={
                                        FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.other_provider_location
                                    }
                                    hasError={!!errors?.other_provider_location}
                                    errorText={
                                        errors?.other_provider_location?.message
                                    }
                                    defaultValue={
                                        noteDetails.note_content
                                            ?.other_provider_location
                                    }
                                />
                            </div>
                        ) : null}
                    </>
                )}
                {watch("appointment_location") ===
                AppointmentLocation.TELETHERAPY ? (
                    <div className="note-checkboxes">
                        <FormCheckbox
                            {...register(
                                "consent_was_received_for_the_teletherapy_session"
                            )}
                            label={
                                FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.consent_was_received_for_the_teletherapy_session
                            }
                            defaultChecked={
                                noteDetails.note_content
                                    ?.consent_was_received_for_the_teletherapy_session
                            }
                        />
                        <FormCheckbox
                            {...register(
                                "the_teletherapy_session_was_conducted_via_video"
                            )}
                            label={
                                FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.the_teletherapy_session_was_conducted_via_video
                            }
                            defaultChecked={
                                noteDetails.note_content
                                    ?.the_teletherapy_session_was_conducted_via_video
                            }
                        />
                    </div>
                ) : null}
                <div className="fg">
                    <Controller
                        name="diagnosis_codes"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.diagnosis_codes
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={
                                    errors?.diagnosis_codes?.type ===
                                    "typeError"
                                        ? "Diagnosis codes are required"
                                        : errors?.diagnosis_codes?.message
                                }
                                isMulti
                                hideSelectedOptions
                                isRequired
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="secondary_diagnosis"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.secondary_diagnosis
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={errors.secondary_diagnosis?.message}
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="tertiary_diagnosis"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.tertiary_diagnosis
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={errors.tertiary_diagnosis?.message}
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="additional_diagnosis_one"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.additional_diagnosis_one
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={
                                    errors.additional_diagnosis_one?.message
                                }
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="additional_diagnosis_two"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.additional_diagnosis_two
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={
                                    errors.additional_diagnosis_two?.message
                                }
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <h3 className="note-header">Identities</h3>
                {Array.from({ length: fields.length }, (_, i) => (
                    <React.Fragment key={`${i + 1} key`}>
                        <p className="note-header">
                            {i === 0
                                ? `Client - ${clientName}`
                                : `Collateral ${i}`}
                        </p>
                        <section id="step-one" className="mb-10">
                            {i !== 0 && (
                                <div className="fg">
                                    <Input
                                        {...register(`family.${i}.name`)}
                                        label="Name"
                                        defaultValue={
                                            watch(`family.${i}.name`) || ""
                                        }
                                        isRequired
                                        hasError={!!errors?.family?.[i]?.name}
                                        errorText={
                                            errors?.family?.[i]?.name?.message
                                        }
                                    />
                                </div>
                            )}
                            <div className="fg">
                                <Input
                                    {...register(`family.${i}.preferred_name`)}
                                    label={
                                        FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_FAMILY_NOTE_DETAILS.preferred_name
                                    }
                                    value={watch(`family.${i}.preferred_name`)}
                                    isOptional
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.gender`}
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            label="Gender"
                                            placeholder=""
                                            value={
                                                GenderForSelect.find(
                                                    (gender) =>
                                                        gender.value ===
                                                            watch(
                                                                `family.${i}.gender`
                                                            ) || ""
                                                ) || null
                                            }
                                            onChange={(val) =>
                                                field.onChange(
                                                    (val as Option).value
                                                )
                                            }
                                            options={GenderForSelect}
                                            hasError={
                                                !!errors?.family?.[i]?.gender
                                            }
                                            errorText={
                                                errors?.family?.[i]?.gender
                                                    ?.message
                                            }
                                            isRequired
                                        />
                                    )}
                                />
                            </div>
                            {watch(`family.${i}.gender`) === "other" ? (
                                <div className="fg">
                                    <Input
                                        {...register(
                                            `family.${i}.other_gender`
                                        )}
                                        label={
                                            FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_FAMILY_NOTE_DETAILS.other_gender
                                        }
                                        value={watch(
                                            `family.${i}.other_gender`
                                        )}
                                        hasError={
                                            !!errors?.family?.[i]?.other_gender
                                        }
                                        errorText={
                                            errors?.family?.[i]?.other_gender
                                                ?.message
                                        }
                                        isRequired
                                    />
                                </div>
                            ) : null}
                            <div className="fg">
                                <Input
                                    {...register(`family.${i}.pronouns`)}
                                    label={
                                        FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_FAMILY_NOTE_DETAILS.pronouns
                                    }
                                    value={watch(`family.${i}.pronouns`)}
                                    isOptional
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.religion`}
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            label="Religion"
                                            placeholder=""
                                            value={
                                                ReligionForSelect.find(
                                                    (religion) =>
                                                        religion.value ===
                                                            watch(
                                                                `family.${i}.religion`
                                                            ) || ""
                                                ) || null
                                            }
                                            onChange={(val) => {
                                                if (val === null) {
                                                    field.onChange("");
                                                    return;
                                                }
                                                field.onChange(
                                                    (val as Option).value
                                                );
                                            }}
                                            options={ReligionForSelect}
                                            isOptional
                                            isClearable
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.education`}
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            label="Education"
                                            placeholder=""
                                            value={
                                                EducationForSelect.find(
                                                    (education) =>
                                                        education.value ===
                                                        watch(
                                                            `family.${i}.education` ||
                                                                ""
                                                        )
                                                ) || null
                                            }
                                            onChange={(val) => {
                                                if (val === null) {
                                                    field.onChange("");
                                                    return;
                                                }
                                                field.onChange(
                                                    (val as Option).value
                                                );
                                            }}
                                            options={EducationForSelect}
                                            isOptional
                                            isClearable
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.race`}
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            label="Race"
                                            placeholder=""
                                            value={
                                                RaceForSelect.find(
                                                    (race) =>
                                                        race.value ===
                                                            watch(
                                                                `family.${i}.race`
                                                            ) || ""
                                                ) || null
                                            }
                                            onChange={(val) => {
                                                if (val === null) {
                                                    field.onChange("");
                                                    return;
                                                }
                                                field.onChange(
                                                    (val as Option).value
                                                );
                                            }}
                                            options={RaceForSelect}
                                            isOptional
                                            isClearable
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.ethinicity`}
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            label="Ethnicity"
                                            value={watch(
                                                `family.${i}.ethinicity`
                                            )}
                                            onChange={field.onChange}
                                            isOptional
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.sexual_orientation`}
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            label="Sexual orientation"
                                            placeholder=""
                                            value={
                                                SexualOrientationForSelect.find(
                                                    (orientation) =>
                                                        orientation.value ===
                                                            watch(
                                                                `family.${i}.sexual_orientation`
                                                            ) || ""
                                                ) || null
                                            }
                                            onChange={(val) => {
                                                if (val === null) {
                                                    field.onChange("");
                                                    return;
                                                }
                                                field.onChange(
                                                    (val as Option).value
                                                );
                                            }}
                                            options={SexualOrientationForSelect}
                                            isOptional
                                            isClearable
                                        />
                                    )}
                                />
                            </div>
                            {watch(`family.${i}.sexual_orientation`) ===
                            "other" ? (
                                <div className="fg">
                                    <Input
                                        {...register(
                                            `family.${i}.other_sexual_orientation`
                                        )}
                                        label={
                                            FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_FAMILY_NOTE_DETAILS.other_sexual_orientation
                                        }
                                        value={watch(
                                            `family.${i}.other_sexual_orientation`
                                        )}
                                        hasError={
                                            !!errors?.family?.[i]
                                                ?.other_sexual_orientation
                                        }
                                        errorText={
                                            errors?.family?.[i]
                                                ?.other_sexual_orientation
                                                ?.message
                                        }
                                        isRequired
                                    />
                                </div>
                            ) : null}
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.current_employment`}
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            label="Current employment"
                                            value={watch(
                                                `family.${i}.current_employment`
                                            )}
                                            onChange={field.onChange}
                                            isOptional
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.current_employment_details`}
                                    control={control}
                                    render={({ field }) => (
                                        <TextArea
                                            label="Details"
                                            value={watch(
                                                `family.${i}.current_employment_details`
                                            )}
                                            onChange={field.onChange}
                                            isOptional
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.relationship_status`}
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            label="Relationship status"
                                            placeholder=""
                                            value={
                                                RelationshipForSelect.find(
                                                    (relationship) =>
                                                        relationship.value ===
                                                            watch(
                                                                `family.${i}.relationship_status`
                                                            ) || ""
                                                ) || null
                                            }
                                            onChange={(val) => {
                                                if (val === null) {
                                                    field.onChange("");
                                                    return;
                                                }
                                                field.onChange(
                                                    (val as Option).value
                                                );
                                            }}
                                            options={RelationshipForSelect}
                                            isOptional
                                            isClearable
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.name_of_partner`}
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            label="Name of partner"
                                            value={watch(
                                                `family.${i}.name_of_partner`
                                            )}
                                            onChange={field.onChange}
                                            isOptional
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.emergency_contact_information`}
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            label="Emergency contact information"
                                            value={watch(
                                                `family.${i}.emergency_contact_information`
                                            )}
                                            onChange={field.onChange}
                                            isOptional
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.legal_problems`}
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            label="Legal problems"
                                            value={watch(
                                                `family.${i}.legal_problems`
                                            )}
                                            onChange={field.onChange}
                                            isOptional
                                        />
                                    )}
                                />
                            </div>
                        </section>
                    </React.Fragment>
                ))}
                <h3 className="note-label-req">Presenting Concerns</h3>
                <p className="note-label">
                    {
                        FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.what_are_the_problem_you_are_seeking_help_for
                    }
                </p>
                <div className="fg">
                    <TextArea
                        {...register(
                            `what_are_the_problem_you_are_seeking_help_for`
                        )}
                        defaultValue={watch(
                            `what_are_the_problem_you_are_seeking_help_for`
                        )}
                        hasError={
                            !!errors.what_are_the_problem_you_are_seeking_help_for
                        }
                        errorText={
                            errors.what_are_the_problem_you_are_seeking_help_for
                                ?.message
                        }
                        isOptional
                    />
                </div>
                <p className="note-label note-label-opt">Symptoms</p>
                {Array.from({ length: fields.length }, (_, i) => (
                    <React.Fragment key={`${i + 1} key`}>
                        <p className="note-header">
                            {i === 0
                                ? `Client - ${clientName}`
                                : `Collateral ${i}`}{" "}
                            {i !== 0 && watch(`family.${i}.name`)
                                ? `- ${
                                      watch(`family.${i}.name`) ||
                                      noteDetails?.note_content.family[i]?.name
                                  }`
                                : ""}
                        </p>
                        <section id="step-two" className="mb-10">
                            <div className="mb-[--spacing-form-items]">
                                <Controller
                                    name={`family.${i}.anxiety`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Anxiety"
                                            name="Anxiety"
                                            checked={watch(
                                                `family.${i}.anxiety`
                                            )}
                                            onChange={(e) => {
                                                field.onChange(
                                                    e.target.checked
                                                );
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {watch(`family.${i}.anxiety`) && (
                                <div className="fg fg-space-between two flex">
                                    <Controller
                                        name={`family.${i}.anxiety_frequency`}
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                label="Frequency"
                                                placeholder=""
                                                onChange={(val) => {
                                                    if (val === null) {
                                                        field.onChange("");
                                                        return;
                                                    }
                                                    field.onChange(
                                                        (val as Option).value
                                                    );
                                                }}
                                                value={
                                                    FrequencyForSelect.find(
                                                        (frequency) =>
                                                            frequency.value ===
                                                                watch(
                                                                    `family.${i}.anxiety_frequency`
                                                                ) || ""
                                                    ) || null
                                                }
                                                options={FrequencyForSelect}
                                                isClearable
                                            />
                                        )}
                                    />
                                    <TextArea
                                        {...register(
                                            `family.${i}.anxiety_details`
                                        )}
                                        label="Details"
                                        defaultValue={watch(
                                            `family.${i}.anxiety_details`
                                        )}
                                        hasError={
                                            !!errors?.family?.[i]
                                                ?.anxiety_details
                                        }
                                        errorText={
                                            errors?.family?.[i]?.anxiety_details
                                                ?.message
                                        }
                                        isRequired
                                    />
                                </div>
                            )}
                            <div className="mb-[--spacing-form-items]">
                                <Controller
                                    name={`family.${i}.panic_attacks`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Panic attacks"
                                            checked={watch(
                                                `family.${i}.panic_attacks`
                                            )}
                                            onChange={(e) => {
                                                field.onChange(
                                                    e.target.checked
                                                );
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {watch(`family.${i}.panic_attacks`) && (
                                <div className="fg fg-space-between two flex">
                                    <Controller
                                        name={`family.${i}.panic_attacks_freqency`}
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                label="Frequency"
                                                placeholder=""
                                                onChange={(val) => {
                                                    if (val === null) {
                                                        field.onChange("");
                                                        return;
                                                    }
                                                    field.onChange(
                                                        (val as Option).value
                                                    );
                                                }}
                                                value={
                                                    FrequencyForSelect.find(
                                                        (frequency) =>
                                                            frequency.value ===
                                                                watch(
                                                                    `family.${i}.panic_attacks_freqency`
                                                                ) || ""
                                                    ) || null
                                                }
                                                options={FrequencyForSelect}
                                                isClearable
                                            />
                                        )}
                                    />
                                    <TextArea
                                        {...register(
                                            `family.${i}.panic_attacks_details`
                                        )}
                                        label="Details"
                                        defaultValue={watch(
                                            `family.${i}.panic_attacks_details`
                                        )}
                                        hasError={
                                            !!errors?.family?.[i]
                                                ?.panic_attacks_details
                                        }
                                        errorText={
                                            errors?.family?.[i]
                                                ?.panic_attacks_details?.message
                                        }
                                        isRequired
                                    />
                                </div>
                            )}
                            <div className="mb-[--spacing-form-items]">
                                <Controller
                                    name={`family.${i}.depression`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Depression"
                                            checked={watch(
                                                `family.${i}.depression`
                                            )}
                                            onChange={(e) =>
                                                field.onChange(e.target.checked)
                                            }
                                        />
                                    )}
                                />
                            </div>
                            {watch(`family.${i}.depression`) && (
                                <div className="fg fg-space-between two flex">
                                    <Controller
                                        name={`family.${i}.depression_freqency`}
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                label="Frequency"
                                                placeholder=""
                                                onChange={(val) => {
                                                    if (val === null) {
                                                        field.onChange("");
                                                        return;
                                                    }
                                                    field.onChange(
                                                        (val as Option).value
                                                    );
                                                }}
                                                value={
                                                    FrequencyForSelect.find(
                                                        (frequency) =>
                                                            frequency.value ===
                                                                watch(
                                                                    `family.${i}.depression_freqency`
                                                                ) || ""
                                                    ) || null
                                                }
                                                options={FrequencyForSelect}
                                                isClearable
                                            />
                                        )}
                                    />
                                    <TextArea
                                        {...register(
                                            `family.${i}.depression_details`
                                        )}
                                        label="Details"
                                        defaultValue={watch(
                                            `family.${i}.depression_details`
                                        )}
                                        hasError={
                                            !!errors?.family?.[i]
                                                ?.depression_details
                                        }
                                        errorText={
                                            errors?.family?.[i]
                                                ?.depression_details?.message
                                        }
                                        isRequired
                                    />
                                </div>
                            )}
                            <div className="note-checkboxes-list">
                                <Controller
                                    name={`family.${i}.selected_symptoms`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Easily distracted"
                                            value="Easily distracted"
                                            checked={(
                                                watch(
                                                    `family.${i}.selected_symptoms`
                                                ) || []
                                            )?.includes("Easily distracted")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.selected_symptoms`
                                                          ) || []),
                                                          "Easily distracted",
                                                      ]
                                                    : watch(
                                                          `family.${i}.selected_symptoms`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Easily distracted"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />

                                <Controller
                                    name={`family.${i}.selected_symptoms`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Impulsive"
                                            value="Impulsive"
                                            checked={(
                                                watch(
                                                    `family.${i}.selected_symptoms`
                                                ) || []
                                            )?.includes("Impulsive")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.selected_symptoms`
                                                          ) || []),
                                                          "Impulsive",
                                                      ]
                                                    : watch(
                                                          `family.${i}.selected_symptoms`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Impulsive"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.selected_symptoms`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Paranoia"
                                            value="Paranoia"
                                            checked={(
                                                watch(
                                                    `family.${i}.selected_symptoms`
                                                ) || []
                                            )?.includes("Paranoia")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.selected_symptoms`
                                                          ) || []),
                                                          "Paranoia",
                                                      ]
                                                    : watch(
                                                          `family.${i}.selected_symptoms`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Paranoia"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.selected_symptoms`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Alcohol"
                                            value="Alcohol"
                                            checked={(
                                                watch(
                                                    `family.${i}.selected_symptoms`
                                                ) || []
                                            )?.includes("Alcohol")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.selected_symptoms`
                                                          ) || []),
                                                          "Alcohol",
                                                      ]
                                                    : watch(
                                                          `family.${i}.selected_symptoms`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Alcohol"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />

                                <Controller
                                    name={`family.${i}.selected_symptoms`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Anger outburst"
                                            value="Anger outburst"
                                            checked={(
                                                watch(
                                                    `family.${i}.selected_symptoms`
                                                ) || []
                                            )?.includes("Anger outburst")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.selected_symptoms`
                                                          ) || []),
                                                          "Anger outburst",
                                                      ]
                                                    : watch(
                                                          `family.${i}.selected_symptoms`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Anger outburst"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />

                                <Controller
                                    name={`family.${i}.selected_symptoms`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Unable to feel pleasure"
                                            value="Unable to feel pleasure"
                                            checked={(
                                                watch(
                                                    `family.${i}.selected_symptoms`
                                                ) || []
                                            )?.includes(
                                                "Unable to feel pleasure"
                                            )}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.selected_symptoms`
                                                          ) || []),
                                                          "Unable to feel pleasure",
                                                      ]
                                                    : watch(
                                                          `family.${i}.selected_symptoms`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Unable to feel pleasure"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.selected_symptoms`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Excessive energy"
                                            value="Excessive energy"
                                            checked={(
                                                watch(
                                                    `family.${i}.selected_symptoms`
                                                ) || []
                                            )?.includes("Excessive energy")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.selected_symptoms`
                                                          ) || []),
                                                          "Excessive energy",
                                                      ]
                                                    : watch(
                                                          `family.${i}.selected_symptoms`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Excessive energy"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.selected_symptoms`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Recreational drug use"
                                            value="Recreational drug use"
                                            checked={(
                                                watch(
                                                    `family.${i}.selected_symptoms`
                                                ) || []
                                            )?.includes(
                                                "Recreational drug use"
                                            )}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.selected_symptoms`
                                                          ) || []),
                                                          "Recreational drug use",
                                                      ]
                                                    : watch(
                                                          `family.${i}.selected_symptoms`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Recreational drug use"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.selected_symptoms`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Tobacco"
                                            value="Tobacco"
                                            checked={(
                                                watch(
                                                    `family.${i}.selected_symptoms`
                                                ) || []
                                            )?.includes("Tobacco")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.selected_symptoms`
                                                          ) || []),
                                                          "Tobacco",
                                                      ]
                                                    : watch(
                                                          `family.${i}.selected_symptoms`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Tobacco"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.selected_symptoms`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Racing thoughts"
                                            value="Racing thoughts"
                                            checked={(
                                                watch(
                                                    `family.${i}.selected_symptoms`
                                                ) || []
                                            )?.includes("Racing thoughts")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.selected_symptoms`
                                                          ) || []),
                                                          "Racing thoughts",
                                                      ]
                                                    : watch(
                                                          `family.${i}.selected_symptoms`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Racing thoughts"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.other_symptoms`}
                                    control={control}
                                    render={({ field }) => (
                                        <TextArea
                                            label="Other symptoms"
                                            value={watch(
                                                `family.${i}.other_symptoms`
                                            )}
                                            onChange={field.onChange}
                                            isOptional
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.physical_symptoms`}
                                    control={control}
                                    render={({ field }) => (
                                        <TextArea
                                            label="Physical symptoms"
                                            value={watch(
                                                `family.${i}.physical_symptoms`
                                            )}
                                            onChange={field.onChange}
                                            isOptional
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.medication_physical_psychiatric`}
                                    control={control}
                                    render={({ field }) => (
                                        <TextArea
                                            label="Medication physical/psychiatric"
                                            value={watch(
                                                `family.${i}.medication_physical_psychiatric`
                                            )}
                                            onChange={field.onChange}
                                            isOptional
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.sleeping_habits_and_concerns`}
                                    control={control}
                                    render={({ field }) => (
                                        <TextArea
                                            label="Sleeping habits and concerns"
                                            value={watch(
                                                `family.${i}.sleeping_habits_and_concerns`
                                            )}
                                            onChange={field.onChange}
                                            isOptional
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.difficulties_with_appetite_or_eating_patterns`}
                                    control={control}
                                    render={({ field }) => (
                                        <TextArea
                                            label="Difficulties with appetite or eating patterns"
                                            value={watch(
                                                `family.${i}.difficulties_with_appetite_or_eating_patterns`
                                            )}
                                            onChange={field.onChange}
                                            isOptional
                                        />
                                    )}
                                />
                            </div>
                        </section>
                    </React.Fragment>
                ))}
                {Array.from({ length: fields.length }, (_, i) => (
                    <React.Fragment key={`${i + 1} key`}>
                        <section id="step-three" className="mb-10">
                            <h3 className="note-header">Family history</h3>
                            <p className="note-label">
                                Describe family situation
                            </p>
                            <p className="note-header">
                                {i === 0
                                    ? `Client - ${clientName}`
                                    : `Collateral ${i}`}{" "}
                                {i !== 0 && watch(`family.${i}.name`)
                                    ? `- ${
                                          watch(`family.${i}.name`) ||
                                          noteDetails?.note_content.family[i]
                                              ?.name
                                      }`
                                    : ""}
                            </p>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.parents_stepparents_history`}
                                    control={control}
                                    render={({ field }) => (
                                        <TextArea
                                            label="Parents/Stepparents"
                                            value={watch(
                                                `family.${i}.parents_stepparents_history`
                                            )}
                                            onChange={field.onChange}
                                            isOptional
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.siblings_history`}
                                    control={control}
                                    render={({ field }) => (
                                        <TextArea
                                            label="Siblings"
                                            value={watch(
                                                `family.${i}.siblings_history`
                                            )}
                                            onChange={field.onChange}
                                            isOptional
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.grandparents_history`}
                                    control={control}
                                    render={({ field }) => (
                                        <TextArea
                                            label="Grandparents"
                                            value={watch(
                                                `family.${i}.grandparents_history`
                                            )}
                                            onChange={field.onChange}
                                            isOptional
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.children_history`}
                                    control={control}
                                    render={({ field }) => (
                                        <TextArea
                                            label="Children"
                                            value={watch(
                                                `family.${i}.children_history`
                                            )}
                                            onChange={field.onChange}
                                            isOptional
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.other_family_members_history`}
                                    control={control}
                                    render={({ field }) => (
                                        <TextArea
                                            label="Other family members"
                                            value={watch(
                                                `family.${i}.other_family_members_history`
                                            )}
                                            onChange={field.onChange}
                                            isOptional
                                        />
                                    )}
                                />
                            </div>
                            <h3 className="note-header">
                                {i === 0
                                    ? `Client - ${clientName}`
                                    : `Collateral ${i}`}{" "}
                                {i !== 0 && watch(`family.${i}.name`)
                                    ? `- ${
                                          watch(`family.${i}.name`) ||
                                          noteDetails?.note_content.family[i]
                                              ?.name
                                      }`
                                    : ""}
                            </h3>
                            <p className="note-label">
                                Select which family members the below situation
                                apply to
                            </p>
                            <h3 className="note-label note-label-opt">
                                Alcohol abuse
                            </h3>
                            <div className="note-checkboxes-list">
                                <Controller
                                    name={`family.${i}.family_members_alcohol_abuse`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="All"
                                            value="All"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_alcohol_abuse`
                                                ) || []
                                            )?.includes("All")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_alcohol_abuse`
                                                          ) || []),
                                                          "All",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_alcohol_abuse`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !== "All"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />

                                <Controller
                                    name={`family.${i}.family_members_alcohol_abuse`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Father"
                                            value="Father"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_alcohol_abuse`
                                                ) || []
                                            )?.includes("Father")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_alcohol_abuse`
                                                          ) || []),
                                                          "Father",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_alcohol_abuse`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Father"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_alcohol_abuse`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Mother"
                                            value="Mother"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_alcohol_abuse`
                                                ) || []
                                            )?.includes("Mother")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_alcohol_abuse`
                                                          ) || []),
                                                          "Mother",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_alcohol_abuse`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Mother"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_alcohol_abuse`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Child/Children"
                                            value="Child/Children"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_alcohol_abuse`
                                                ) || []
                                            )?.includes("Child/Children")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_alcohol_abuse`
                                                          ) || []),
                                                          "Child/Children",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_alcohol_abuse`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Child/Children"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_alcohol_abuse`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Brother"
                                            value="Brother"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_alcohol_abuse`
                                                ) || []
                                            )?.includes("Brother")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_alcohol_abuse`
                                                          ) || []),
                                                          "Brother",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_alcohol_abuse`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Brother"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_alcohol_abuse`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Sister"
                                            value="Sister"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_alcohol_abuse`
                                                ) || []
                                            )?.includes("Sister")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_alcohol_abuse`
                                                          ) || []),
                                                          "Sister",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_alcohol_abuse`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Sister"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_alcohol_abuse`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Other"
                                            value="Other"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_alcohol_abuse`
                                                ) || []
                                            )?.includes("Other")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_alcohol_abuse`
                                                          ) || []),
                                                          "Other",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_alcohol_abuse`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Other"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.family_members_alcohol_abuse_other`}
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            label={
                                                FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_FAMILY_NOTE_DETAILS.family_members_alcohol_abuse_other
                                            }
                                            value={watch(
                                                `family.${i}.family_members_alcohol_abuse_other`
                                            )}
                                            onChange={field.onChange}
                                            isOptional
                                        />
                                    )}
                                />
                            </div>
                            <h3 className="note-label note-label-opt">
                                Anxiety
                            </h3>
                            <div className="note-checkboxes-list">
                                <Controller
                                    name={`family.${i}.family_members_anxiety`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="All"
                                            value="All"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_anxiety`
                                                ) || []
                                            )?.includes("All")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_anxiety`
                                                          ) || []),
                                                          "All",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_anxiety`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !== "All"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_anxiety`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Father"
                                            value="Father"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_anxiety`
                                                ) || []
                                            )?.includes("Father")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_anxiety`
                                                          ) || []),
                                                          "Father",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_anxiety`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Father"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_anxiety`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Mother"
                                            value="Mother"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_anxiety`
                                                ) || []
                                            )?.includes("Mother")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_anxiety`
                                                          ) || []),
                                                          "Mother",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_anxiety`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Mother"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_anxiety`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Child/Children"
                                            value="Child/Children"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_anxiety`
                                                ) || []
                                            )?.includes("Child/Children")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_anxiety`
                                                          ) || []),
                                                          "Child/Children",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_anxiety`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Child/Children"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_anxiety`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Brother"
                                            value="Brother"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_anxiety`
                                                ) || []
                                            )?.includes("Brother")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_anxiety`
                                                          ) || []),
                                                          "Brother",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_anxiety`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Brother"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_anxiety`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Sister"
                                            value="Sister"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_anxiety`
                                                ) || []
                                            )?.includes("Sister")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_anxiety`
                                                          ) || []),
                                                          "Sister",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_anxiety`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Sister"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_anxiety`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Other"
                                            value="Other"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_anxiety`
                                                ) || []
                                            )?.includes("Other")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_anxiety`
                                                          ) || []),
                                                          "Other",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_anxiety`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Other"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.family_members_anxiety_other`}
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            label={
                                                FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_FAMILY_NOTE_DETAILS.family_members_anxiety_other
                                            }
                                            value={watch(
                                                `family.${i}.family_members_anxiety_other`
                                            )}
                                            onChange={field.onChange}
                                            isOptional
                                        />
                                    )}
                                />
                            </div>
                            <h3 className="note-label note-label-opt">
                                Bipolar Disorder
                            </h3>
                            <div className="note-checkboxes-list">
                                <Controller
                                    name={`family.${i}.family_members_bipolar_disorder`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="All"
                                            value="All"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_bipolar_disorder`
                                                ) || []
                                            )?.includes("All")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_bipolar_disorder`
                                                          ) || []),
                                                          "All",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_bipolar_disorder`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !== "All"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_bipolar_disorder`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Father"
                                            value="Father"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_bipolar_disorder`
                                                ) || []
                                            )?.includes("Father")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_bipolar_disorder`
                                                          ) || []),
                                                          "Father",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_bipolar_disorder`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Father"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_bipolar_disorder`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Mother"
                                            value="Mother"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_bipolar_disorder`
                                                ) || []
                                            )?.includes("Mother")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_bipolar_disorder`
                                                          ) || []),
                                                          "Mother",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_bipolar_disorder`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Mother"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_bipolar_disorder`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Child/Children"
                                            value="Child/Children"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_bipolar_disorder`
                                                ) || []
                                            )?.includes("Child/Children")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_bipolar_disorder`
                                                          ) || []),
                                                          "Child/Children",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_bipolar_disorder`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Child/Children"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_bipolar_disorder`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Brother"
                                            value="Brother"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_bipolar_disorder`
                                                ) || []
                                            )?.includes("Brother")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_bipolar_disorder`
                                                          ) || []),
                                                          "Brother",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_bipolar_disorder`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Brother"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_bipolar_disorder`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Sister"
                                            value="Sister"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_bipolar_disorder`
                                                ) || []
                                            )?.includes("Sister")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_bipolar_disorder`
                                                          ) || []),
                                                          "Sister",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_bipolar_disorder`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Sister"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_bipolar_disorder`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Other"
                                            value="Other"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_bipolar_disorder`
                                                ) || []
                                            )?.includes("Other")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_bipolar_disorder`
                                                          ) || []),
                                                          "Other",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_bipolar_disorder`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Other"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.family_members_bipolar_disorder_other`}
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            label={
                                                FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_FAMILY_NOTE_DETAILS.family_members_bipolar_disorder_other
                                            }
                                            value={watch(
                                                `family.${i}.family_members_bipolar_disorder_other`
                                            )}
                                            onChange={field.onChange}
                                            isOptional
                                        />
                                    )}
                                />
                            </div>
                            <h3 className="note-label note-label-opt">
                                Depression
                            </h3>
                            <div className="note-checkboxes-list">
                                <Controller
                                    name={`family.${i}.family_members_depression`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="All"
                                            value="All"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_depression`
                                                ) || []
                                            )?.includes("All")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_depression`
                                                          ) || []),
                                                          "All",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_depression`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !== "All"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_depression`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Father"
                                            value="Father"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_depression`
                                                ) || []
                                            )?.includes("Father")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_depression`
                                                          ) || []),
                                                          "Father",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_depression`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Father"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_depression`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Mother"
                                            value="Mother"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_depression`
                                                ) || []
                                            )?.includes("Mother")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_depression`
                                                          ) || []),
                                                          "Mother",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_depression`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Mother"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_depression`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Child/Children"
                                            value="Child/Children"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_depression`
                                                ) || []
                                            )?.includes("Child/Children")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_depression`
                                                          ) || []),
                                                          "Child/Children",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_depression`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Child/Children"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_depression`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Brother"
                                            value="Brother"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_depression`
                                                ) || []
                                            )?.includes("Brother")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_depression`
                                                          ) || []),
                                                          "Brother",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_depression`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Brother"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_depression`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Sister"
                                            value="Sister"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_depression`
                                                ) || []
                                            )?.includes("Sister")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_depression`
                                                          ) || []),
                                                          "Sister",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_depression`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Sister"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_depression`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Other"
                                            value="Other"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_depression`
                                                ) || []
                                            )?.includes("Other")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_depression`
                                                          ) || []),
                                                          "Other",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_depression`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Other"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.family_members_depression_other`}
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            label={
                                                FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_FAMILY_NOTE_DETAILS.family_members_depression_other
                                            }
                                            value={watch(
                                                `family.${i}.family_members_depression_other`
                                            )}
                                            onChange={field.onChange}
                                            isOptional
                                        />
                                    )}
                                />
                            </div>
                            <h3 className="note-label note-label-opt">PTSD</h3>
                            <div className="note-checkboxes-list">
                                <Controller
                                    name={`family.${i}.family_members_ptsd`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="All"
                                            value="All"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_ptsd`
                                                ) || []
                                            )?.includes("All")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_ptsd`
                                                          ) || []),
                                                          "All",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_ptsd`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !== "All"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_ptsd`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Father"
                                            value="Father"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_ptsd`
                                                ) || []
                                            )?.includes("Father")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_ptsd`
                                                          ) || []),
                                                          "Father",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_ptsd`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Father"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_ptsd`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Mother"
                                            value="Mother"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_ptsd`
                                                ) || []
                                            )?.includes("Mother")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_ptsd`
                                                          ) || []),
                                                          "Mother",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_ptsd`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Mother"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_ptsd`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Child/Children"
                                            value="Child/Children"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_ptsd`
                                                ) || []
                                            )?.includes("Child/Children")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_ptsd`
                                                          ) || []),
                                                          "Child/Children",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_ptsd`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Child/Children"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_ptsd`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Brother"
                                            value="Brother"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_ptsd`
                                                ) || []
                                            )?.includes("Brother")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_ptsd`
                                                          ) || []),
                                                          "Brother",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_ptsd`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Brother"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_ptsd`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Sister"
                                            value="Sister"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_ptsd`
                                                ) || []
                                            )?.includes("Sister")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_ptsd`
                                                          ) || []),
                                                          "Sister",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_ptsd`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Sister"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_ptsd`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Other"
                                            value="Other"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_ptsd`
                                                ) || []
                                            )?.includes("Other")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_ptsd`
                                                          ) || []),
                                                          "Other",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_ptsd`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Other"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.family_members_ptsd_other`}
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            label={
                                                FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_FAMILY_NOTE_DETAILS.family_members_ptsd_other
                                            }
                                            value={watch(
                                                `family.${i}.family_members_ptsd_other`
                                            )}
                                            onChange={field.onChange}
                                            isOptional
                                        />
                                    )}
                                />
                            </div>
                            <h3 className="note-label note-label-opt">
                                Schizophrenia
                            </h3>
                            <div className="note-checkboxes-list">
                                <Controller
                                    name={`family.${i}.family_members_schizophrenia`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="All"
                                            value="All"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_schizophrenia`
                                                ) || []
                                            )?.includes("All")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_schizophrenia`
                                                          ) || []),
                                                          "All",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_schizophrenia`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !== "All"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_schizophrenia`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Father"
                                            value="Father"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_schizophrenia`
                                                ) || []
                                            )?.includes("Father")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_schizophrenia`
                                                          ) || []),
                                                          "Father",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_schizophrenia`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Father"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_schizophrenia`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Mother"
                                            value="Mother"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_schizophrenia`
                                                ) || []
                                            )?.includes("Mother")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_schizophrenia`
                                                          ) || []),
                                                          "Mother",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_schizophrenia`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Mother"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_schizophrenia`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Child/Children"
                                            value="Child/Children"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_schizophrenia`
                                                ) || []
                                            )?.includes("Child/Children")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_schizophrenia`
                                                          ) || []),
                                                          "Child/Children",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_schizophrenia`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Child/Children"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_schizophrenia`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Brother"
                                            value="Brother"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_schizophrenia`
                                                ) || []
                                            )?.includes("Brother")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_schizophrenia`
                                                          ) || []),
                                                          "Brother",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_schizophrenia`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Brother"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_schizophrenia`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Sister"
                                            value="Sister"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_schizophrenia`
                                                ) || []
                                            )?.includes("Sister")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_schizophrenia`
                                                          ) || []),
                                                          "Sister",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_schizophrenia`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Sister"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_schizophrenia`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Other"
                                            value="Other"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_schizophrenia`
                                                ) || []
                                            )?.includes("Other")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_schizophrenia`
                                                          ) || []),
                                                          "Other",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_schizophrenia`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Other"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.family_members_schizophrenia_other`}
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            label={
                                                FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_FAMILY_NOTE_DETAILS.family_members_schizophrenia_other
                                            }
                                            value={watch(
                                                `family.${i}.family_members_schizophrenia_other`
                                            )}
                                            onChange={field.onChange}
                                            isOptional
                                        />
                                    )}
                                />
                            </div>
                            <h3 className="note-label note-label-opt">
                                Substance use
                            </h3>
                            <div className="note-checkboxes-list">
                                <Controller
                                    name={`family.${i}.family_members_substance_abuse`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="All"
                                            value="All"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_substance_abuse`
                                                ) || []
                                            )?.includes("All")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_substance_abuse`
                                                          ) || []),
                                                          "All",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_substance_abuse`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !== "All"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_substance_abuse`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Father"
                                            value="Father"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_substance_abuse`
                                                ) || []
                                            )?.includes("Father")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_substance_abuse`
                                                          ) || []),
                                                          "Father",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_substance_abuse`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Father"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_substance_abuse`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Mother"
                                            value="Mother"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_substance_abuse`
                                                ) || []
                                            )?.includes("Mother")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_substance_abuse`
                                                          ) || []),
                                                          "Mother",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_substance_abuse`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Mother"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_substance_abuse`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Child/Children"
                                            value="Child/Children"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_substance_abuse`
                                                ) || []
                                            )?.includes("Child/Children")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_substance_abuse`
                                                          ) || []),
                                                          "Child/Children",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_substance_abuse`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Child/Children"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_substance_abuse`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Brother"
                                            value="Brother"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_substance_abuse`
                                                ) || []
                                            )?.includes("Brother")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_substance_abuse`
                                                          ) || []),
                                                          "Brother",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_substance_abuse`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Brother"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_substance_abuse`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Sister"
                                            value="Sister"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_substance_abuse`
                                                ) || []
                                            )?.includes("Sister")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_substance_abuse`
                                                          ) || []),
                                                          "Sister",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_substance_abuse`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Sister"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.family_members_substance_abuse`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            label="Other"
                                            value="Other"
                                            checked={(
                                                watch(
                                                    `family.${i}.family_members_substance_abuse`
                                                ) || []
                                            )?.includes("Other")}
                                            onChange={(e) => {
                                                const updatedValue = e.target
                                                    .checked
                                                    ? [
                                                          ...(watch(
                                                              `family.${i}.family_members_substance_abuse`
                                                          ) || []),
                                                          "Other",
                                                      ]
                                                    : watch(
                                                          `family.${i}.family_members_substance_abuse`
                                                      )?.filter(
                                                          (symptom) =>
                                                              symptom !==
                                                              "Other"
                                                      );
                                                field.onChange(updatedValue);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.family_members_substance_abuse_other`}
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            label={
                                                FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_FAMILY_NOTE_DETAILS.family_members_substance_abuse_other
                                            }
                                            value={watch(
                                                `family.${i}.family_members_substance_abuse_other`
                                            )}
                                            onChange={field.onChange}
                                            isOptional
                                        />
                                    )}
                                />
                            </div>
                            <div className="fg">
                                <Controller
                                    name={`family.${i}.family_members_substance_abuse_substance`}
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            label="Specify substance"
                                            value={watch(
                                                `family.${i}.family_members_substance_abuse_substance`
                                            )}
                                            onChange={field.onChange}
                                            isOptional
                                        />
                                    )}
                                />
                            </div>
                        </section>
                    </React.Fragment>
                ))}
                <h3 className="note-header">Mental health history</h3>
                {Array.from(
                    { length: fields.length },
                    (
                        family: FamilyInitialDiagnosticEvaluationNotePayload,
                        i
                    ) => (
                        <React.Fragment key={`${i + 1} key`}>
                            <p className="note-header">
                                {i === 0
                                    ? `Client - ${clientName}`
                                    : `Collateral ${i}`}{" "}
                                {i !== 0 && watch(`family.${i}.name`)
                                    ? `- ${
                                          watch(`family.${i}.name`) ||
                                          family?.name
                                      }`
                                    : ""}
                            </p>
                            <section id="step-four" className="mb-10">
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.trauma_abuse_history`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label="Trauma/abuse history"
                                                value={watch(
                                                    `family.${i}.trauma_abuse_history`
                                                )}
                                                onChange={field.onChange}
                                                isOptional
                                            />
                                        )}
                                    />
                                </div>
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.previous_mental_health_treatment_oupatient`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label="Previous Mental Health Treatment oupatient"
                                                value={watch(
                                                    `family.${i}.previous_mental_health_treatment_oupatient`
                                                )}
                                                onChange={field.onChange}
                                                isOptional
                                            />
                                        )}
                                    />
                                </div>
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.impatient_psychiatric_hospitalization`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label="Impatient/Psychiatric Hospitalization"
                                                value={watch(
                                                    `family.${i}.impatient_psychiatric_hospitalization`
                                                )}
                                                onChange={field.onChange}
                                                isOptional
                                            />
                                        )}
                                    />
                                </div>
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.relevant_medical_history`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label="Relevant Medical History"
                                                value={watch(
                                                    `family.${i}.relevant_medical_history`
                                                )}
                                                onChange={field.onChange}
                                                isOptional
                                            />
                                        )}
                                    />
                                </div>
                            </section>
                        </React.Fragment>
                    )
                )}
                {Array.from({ length: fields.length }, (_, i) => (
                    <React.Fragment key={`${i + 1} key`}>
                        <section id="step-five" className="mb-10">
                            <p className="note-header">
                                Suicide risk assessment
                            </p>
                            <p className="note-header">
                                {i === 0
                                    ? `Client - ${clientName}`
                                    : `Collateral ${i}`}{" "}
                                {i !== 0 && watch(`family.${i}.name`)
                                    ? `- ${
                                          watch(`family.${i}.name`) ||
                                          noteDetails?.note_content.family[i]
                                              ?.name
                                      }`
                                    : ""}
                            </p>
                            <p className="note-label-req">
                                1. Been so distressed you seriously wished to
                                end your life?
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.been_so_distressed_you_seriously_wished_to_end_your_life`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.been_so_distressed_you_seriously_wished_to_end_your_life`}
                                            checked={
                                                watch(
                                                    `family.${i}.been_so_distressed_you_seriously_wished_to_end_your_life`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.been_so_distressed_you_seriously_wished_to_end_your_life`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Today"
                                            value="Today"
                                            name={`family.${i}.been_so_distressed_you_seriously_wished_to_end_your_life`}
                                            checked={
                                                watch(
                                                    `family.${i}.been_so_distressed_you_seriously_wished_to_end_your_life`
                                                ) === "Today"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.been_so_distressed_you_seriously_wished_to_end_your_life`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Recently"
                                            value="Recently"
                                            name={`family.${i}.been_so_distressed_you_seriously_wished_to_end_your_life`}
                                            checked={
                                                watch(
                                                    `family.${i}.been_so_distressed_you_seriously_wished_to_end_your_life`
                                                ) === "Recently"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]
                                ?.been_so_distressed_you_seriously_wished_to_end_your_life ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(
                                `family.${i}.been_so_distressed_you_seriously_wished_to_end_your_life`
                            ) === "Today" ||
                            watch(
                                `family.${i}.been_so_distressed_you_seriously_wished_to_end_your_life`
                            ) === "Recently" ? (
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.describe_been_so_distressed_you_seriously_wished_to_end_your_life`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label="Describe the situation"
                                                value={watch(
                                                    `family.${i}.describe_been_so_distressed_you_seriously_wished_to_end_your_life`
                                                )}
                                                onChange={field.onChange}
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.describe_been_so_distressed_you_seriously_wished_to_end_your_life
                                                }
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.describe_been_so_distressed_you_seriously_wished_to_end_your_life
                                                        ?.message
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            ) : null}
                            <p className="note-label-req">
                                2. Has anything happened recently to make you
                                feel you don’t want to live?
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live`}
                                            checked={
                                                watch(
                                                    `family.${i}.has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Yes"
                                            value="Yes"
                                            name={`family.${i}.has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live`}
                                            checked={
                                                watch(
                                                    `family.${i}.has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live`
                                                ) === "Yes"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]
                                ?.has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(
                                `family.${i}.has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live`
                            ) === "Yes" ? (
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label="Describe the situation"
                                                value={watch(
                                                    `family.${i}.describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live`
                                                )}
                                                onChange={field.onChange}
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live
                                                        ?.message
                                                }
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            ) : null}
                            <p className="note-header">
                                Have you had/Do you have -
                            </p>
                            <p className="note-label-req">
                                3. A specific plan of how you would kill
                                yourself?
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.a_specific_plan_of_how_you_would_kill_yourself`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.a_specific_plan_of_how_you_would_kill_yourself`}
                                            checked={
                                                watch(
                                                    `family.${i}.a_specific_plan_of_how_you_would_kill_yourself`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.a_specific_plan_of_how_you_would_kill_yourself`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Yes"
                                            value="Yes"
                                            name={`family.${i}.a_specific_plan_of_how_you_would_kill_yourself`}
                                            checked={
                                                watch(
                                                    `family.${i}.a_specific_plan_of_how_you_would_kill_yourself`
                                                ) === "Yes"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]
                                ?.a_specific_plan_of_how_you_would_kill_yourself ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(
                                `family.${i}.a_specific_plan_of_how_you_would_kill_yourself`
                            ) === "Yes" ? (
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.describe_a_specific_plan_of_how_you_would_kill_yourself`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label="Describe the situation"
                                                value={watch(
                                                    `family.${i}.describe_a_specific_plan_of_how_you_would_kill_yourself`
                                                )}
                                                onChange={field.onChange}
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.describe_a_specific_plan_of_how_you_would_kill_yourself
                                                }
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.describe_a_specific_plan_of_how_you_would_kill_yourself
                                                        ?.message
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            ) : null}
                            <p className="note-label-req">
                                4. Access to weapons or means of hurting self?
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.access_to_weapons_or_means_of_hurting_self`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.access_to_weapons_or_means_of_hurting_self`}
                                            checked={
                                                watch(
                                                    `family.${i}.access_to_weapons_or_means_of_hurting_self`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.access_to_weapons_or_means_of_hurting_self`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Yes"
                                            value="Yes"
                                            name={`family.${i}.access_to_weapons_or_means_of_hurting_self`}
                                            checked={
                                                watch(
                                                    `family.${i}.access_to_weapons_or_means_of_hurting_self`
                                                ) === "Yes"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]
                                ?.access_to_weapons_or_means_of_hurting_self ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(
                                `family.${i}.access_to_weapons_or_means_of_hurting_self`
                            ) === "Yes" ? (
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.describe_access_to_weapons_or_means_of_hurting_self`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label="Describe the situation"
                                                value={watch(
                                                    `family.${i}.describe_access_to_weapons_or_means_of_hurting_self`
                                                )}
                                                onChange={field.onChange}
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.describe_access_to_weapons_or_means_of_hurting_self
                                                }
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.describe_access_to_weapons_or_means_of_hurting_self
                                                        ?.message
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            ) : null}
                            <p className="note-label-req">
                                5. Made serious suicide attempts
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.made_serious_suicide_attempts`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.made_serious_suicide_attempts`}
                                            checked={
                                                watch(
                                                    `family.${i}.made_serious_suicide_attempts`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.made_serious_suicide_attempts`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Yes"
                                            value="Yes"
                                            name={`family.${i}.made_serious_suicide_attempts`}
                                            checked={
                                                watch(
                                                    `family.${i}.made_serious_suicide_attempts`
                                                ) === "Yes"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]
                                ?.made_serious_suicide_attempts ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(
                                `family.${i}.made_serious_suicide_attempts`
                            ) === "Yes" ? (
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.describe_made_serious_suicide_attempts`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label="Describe the situation"
                                                value={watch(
                                                    `family.${i}.describe_made_serious_suicide_attempts`
                                                )}
                                                onChange={field.onChange}
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.describe_made_serious_suicide_attempts
                                                }
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.describe_made_serious_suicide_attempts
                                                        ?.message
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            ) : null}
                            <p className="note-label-req">
                                6. Purposely done something to hurt yourself?
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.purposely_done_something_to_hurt_yourself`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.purposely_done_something_to_hurt_yourself`}
                                            checked={
                                                watch(
                                                    `family.${i}.purposely_done_something_to_hurt_yourself`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />

                                <Controller
                                    name={`family.${i}.purposely_done_something_to_hurt_yourself`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Yes"
                                            value="Yes"
                                            name={`family.${i}.purposely_done_something_to_hurt_yourself`}
                                            checked={
                                                watch(
                                                    `family.${i}.purposely_done_something_to_hurt_yourself`
                                                ) === "Yes"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]
                                ?.purposely_done_something_to_hurt_yourself ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(
                                `family.${i}.purposely_done_something_to_hurt_yourself`
                            ) === "Yes" ? (
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.describe_done_something_to_hurt_yourself`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label="Describe the situation"
                                                value={watch(
                                                    `family.${i}.describe_done_something_to_hurt_yourself`
                                                )}
                                                onChange={field.onChange}
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.describe_done_something_to_hurt_yourself
                                                }
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.describe_done_something_to_hurt_yourself
                                                        ?.message
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            ) : null}
                            <p className="note-label-req">
                                7. Heard voices telling you to hurt yourself?
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.heard_voices_telling_you_to_hurt_yourself`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.heard_voices_telling_you_to_hurt_yourself`}
                                            checked={
                                                watch(
                                                    `family.${i}.heard_voices_telling_you_to_hurt_yourself`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.heard_voices_telling_you_to_hurt_yourself`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Yes"
                                            value="Yes"
                                            name={`family.${i}.heard_voices_telling_you_to_hurt_yourself`}
                                            checked={
                                                watch(
                                                    `family.${i}.heard_voices_telling_you_to_hurt_yourself`
                                                ) === "Yes"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]
                                ?.heard_voices_telling_you_to_hurt_yourself ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(
                                `family.${i}.heard_voices_telling_you_to_hurt_yourself`
                            ) === "Yes" ? (
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.describe_heard_voices_telling_you_to_hurt_yourself`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label="Describe the situation"
                                                value={watch(
                                                    `family.${i}.describe_heard_voices_telling_you_to_hurt_yourself`
                                                )}
                                                onChange={field.onChange}
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.describe_heard_voices_telling_you_to_hurt_yourself
                                                }
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.describe_heard_voices_telling_you_to_hurt_yourself
                                                        ?.message
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            ) : null}
                            <p className="note-label-req">
                                8. Had relatives who attempted or commited
                                sucide?
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.had_relatives_who_attempted_or_commited_sucide`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.had_relatives_who_attempted_or_commited_sucide`}
                                            checked={
                                                watch(
                                                    `family.${i}.had_relatives_who_attempted_or_commited_sucide`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.had_relatives_who_attempted_or_commited_sucide`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Yes"
                                            value="Yes"
                                            name={`family.${i}.had_relatives_who_attempted_or_commited_sucide`}
                                            checked={
                                                watch(
                                                    `family.${i}.had_relatives_who_attempted_or_commited_sucide`
                                                ) === "Yes"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]
                                ?.had_relatives_who_attempted_or_commited_sucide ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(
                                `family.${i}.had_relatives_who_attempted_or_commited_sucide`
                            ) === "Yes" ? (
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.describe_had_relatives_who_attempted_or_commited_sucide`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label="Describe the situation"
                                                value={watch(
                                                    `family.${i}.describe_had_relatives_who_attempted_or_commited_sucide`
                                                )}
                                                onChange={field.onChange}
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.describe_had_relatives_who_attempted_or_commited_sucide
                                                }
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.describe_had_relatives_who_attempted_or_commited_sucide
                                                        ?.message
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            ) : null}
                            <p className="note-label-req">
                                9. Had thoughts of killing or seriously hurting
                                someone?
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.had_thoughts_of_killing_or_seriously_hurting_someone`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.had_thoughts_of_killing_or_seriously_hurting_someone`}
                                            checked={
                                                watch(
                                                    `family.${i}.had_thoughts_of_killing_or_seriously_hurting_someone`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />

                                <Controller
                                    name={`family.${i}.had_thoughts_of_killing_or_seriously_hurting_someone`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Yes"
                                            value="Yes"
                                            name={`family.${i}.had_thoughts_of_killing_or_seriously_hurting_someone`}
                                            checked={
                                                watch(
                                                    `family.${i}.had_thoughts_of_killing_or_seriously_hurting_someone`
                                                ) === "Yes"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]
                                ?.had_thoughts_of_killing_or_seriously_hurting_someone ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(
                                `family.${i}.had_thoughts_of_killing_or_seriously_hurting_someone`
                            ) === "Yes" ? (
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.describe_had_thoughts_of_killing_or_seriously_hurting_someone`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label="Describe the situation"
                                                value={watch(
                                                    `family.${i}.describe_had_thoughts_of_killing_or_seriously_hurting_someone`
                                                )}
                                                onChange={field.onChange}
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.describe_had_thoughts_of_killing_or_seriously_hurting_someone
                                                }
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.describe_had_thoughts_of_killing_or_seriously_hurting_someone
                                                        ?.message
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            ) : null}
                            <p className="note-label-req">
                                10. Heard voices telling you to hurt others?
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.heard_voices_telling_you_to_hurt_others`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.heard_voices_telling_you_to_hurt_others`}
                                            checked={
                                                watch(
                                                    `family.${i}.heard_voices_telling_you_to_hurt_others`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />

                                <Controller
                                    name={`family.${i}.heard_voices_telling_you_to_hurt_others`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Yes"
                                            value="Yes"
                                            name={`family.${i}.heard_voices_telling_you_to_hurt_others`}
                                            checked={
                                                watch(
                                                    `family.${i}.heard_voices_telling_you_to_hurt_others`
                                                ) === "Yes"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]
                                ?.heard_voices_telling_you_to_hurt_others ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(
                                `family.${i}.heard_voices_telling_you_to_hurt_others`
                            ) === "Yes" ? (
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.describe_heard_voices_telling_you_to_hurt_others`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label="Describe the situation"
                                                value={watch(
                                                    `family.${i}.describe_heard_voices_telling_you_to_hurt_others`
                                                )}
                                                onChange={field.onChange}
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.describe_heard_voices_telling_you_to_hurt_others
                                                }
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.describe_heard_voices_telling_you_to_hurt_others
                                                        ?.message
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            ) : null}
                            <p className="note-label-req">
                                11. Hurt someone or destroyed property on
                                purpose?
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.hurt_someone_or_destroyed_property_on_purpose`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.hurt_someone_or_destroyed_property_on_purpose`}
                                            checked={
                                                watch(
                                                    `family.${i}.hurt_someone_or_destroyed_property_on_purpose`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.hurt_someone_or_destroyed_property_on_purpose`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Yes"
                                            value="Yes"
                                            name={`family.${i}.hurt_someone_or_destroyed_property_on_purpose`}
                                            checked={
                                                watch(
                                                    `family.${i}.hurt_someone_or_destroyed_property_on_purpose`
                                                ) === "Yes"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]
                                ?.hurt_someone_or_destroyed_property_on_purpose ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(
                                `family.${i}.hurt_someone_or_destroyed_property_on_purpose`
                            ) === "Yes" ? (
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.describe_hurt_someone_or_destroyed_property_on_purpose`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label="Describe the situation"
                                                value={watch(
                                                    `family.${i}.describe_hurt_someone_or_destroyed_property_on_purpose`
                                                )}
                                                onChange={field.onChange}
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.describe_hurt_someone_or_destroyed_property_on_purpose
                                                }
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.describe_hurt_someone_or_destroyed_property_on_purpose
                                                        ?.message
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            ) : null}
                            <p className="note-label-req">
                                12. Slapped, kicked, punched someone with intent
                                to harm?
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.slapped_kicked_punched_someone_with_intent_to_harm`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.slapped_kicked_punched_someone_with_intent_to_harm`}
                                            checked={
                                                watch(
                                                    `family.${i}.slapped_kicked_punched_someone_with_intent_to_harm`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.slapped_kicked_punched_someone_with_intent_to_harm`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Yes"
                                            value="Yes"
                                            name={`family.${i}.slapped_kicked_punched_someone_with_intent_to_harm`}
                                            checked={
                                                watch(
                                                    `family.${i}.slapped_kicked_punched_someone_with_intent_to_harm`
                                                ) === "Yes"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]
                                ?.slapped_kicked_punched_someone_with_intent_to_harm ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(
                                `family.${i}.slapped_kicked_punched_someone_with_intent_to_harm`
                            ) === "Yes" ? (
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.describe_slapped_kicked_punched_someone_with_intent_to_harm`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label="Describe the situation"
                                                value={watch(
                                                    `family.${i}.describe_slapped_kicked_punched_someone_with_intent_to_harm`
                                                )}
                                                onChange={field.onChange}
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.describe_slapped_kicked_punched_someone_with_intent_to_harm
                                                }
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.describe_slapped_kicked_punched_someone_with_intent_to_harm
                                                        ?.message
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            ) : null}
                            <p className="note-label-req">
                                13. Been arrested or detained for violent
                                behavior?
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.been_arrested_or_detained_for_violent_behavior`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.been_arrested_or_detained_for_violent_behavior`}
                                            checked={
                                                watch(
                                                    `family.${i}.been_arrested_or_detained_for_violent_behavior`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.been_arrested_or_detained_for_violent_behavior`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Yes"
                                            value="Yes"
                                            name={`family.${i}.been_arrested_or_detained_for_violent_behavior`}
                                            checked={
                                                watch(
                                                    `family.${i}.been_arrested_or_detained_for_violent_behavior`
                                                ) === "Yes"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]
                                ?.been_arrested_or_detained_for_violent_behavior ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(
                                `family.${i}.been_arrested_or_detained_for_violent_behavior`
                            ) === "Yes" ? (
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.describe_been_arrested_or_detained_for_violent_behavior`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label="Describe the situation"
                                                value={watch(
                                                    `family.${i}.describe_been_arrested_or_detained_for_violent_behavior`
                                                )}
                                                onChange={field.onChange}
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.describe_been_arrested_or_detained_for_violent_behavior
                                                }
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.describe_been_arrested_or_detained_for_violent_behavior
                                                        ?.message
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            ) : null}
                            <p className="note-label-req">
                                14. Been to jail for any reason?
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.been_to_jail_for_any_reason`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.been_to_jail_for_any_reason`}
                                            checked={
                                                watch(
                                                    `family.${i}.been_to_jail_for_any_reason`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.been_to_jail_for_any_reason`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Yes"
                                            value="Yes"
                                            name={`family.${i}.been_to_jail_for_any_reason`}
                                            checked={
                                                watch(
                                                    `family.${i}.been_to_jail_for_any_reason`
                                                ) === "Yes"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]?.been_to_jail_for_any_reason ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(
                                `family.${i}.been_to_jail_for_any_reason`
                            ) === "Yes" ? (
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.describe_been_to_jail_for_any_reason`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label="Describe the situation"
                                                value={watch(
                                                    `family.${i}.describe_been_to_jail_for_any_reason`
                                                )}
                                                onChange={field.onChange}
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.describe_been_to_jail_for_any_reason
                                                }
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.describe_been_to_jail_for_any_reason
                                                        ?.message
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            ) : null}
                            <p className="note-label-req">
                                15. Been on probation for any reason?
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.been_on_probation_for_any_reason`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.been_on_probation_for_any_reason`}
                                            checked={
                                                watch(
                                                    `family.${i}.been_on_probation_for_any_reason`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.been_on_probation_for_any_reason`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Yes"
                                            value="Yes"
                                            name={`family.${i}.been_on_probation_for_any_reason`}
                                            checked={
                                                watch(
                                                    `family.${i}.been_on_probation_for_any_reason`
                                                ) === "Yes"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]
                                ?.been_on_probation_for_any_reason ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(
                                `family.${i}.been_on_probation_for_any_reason`
                            ) === "Yes" ? (
                                <div className="fg">
                                    <Controller
                                        name={`family.${i}.describe_been_on_probation_for_any_reason`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextArea
                                                label="Describe the situation"
                                                value={watch(
                                                    `family.${i}.describe_been_on_probation_for_any_reason`
                                                )}
                                                onChange={field.onChange}
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.describe_been_on_probation_for_any_reason
                                                }
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.describe_been_on_probation_for_any_reason
                                                        ?.message
                                                }
                                            />
                                        )}
                                    />
                                </div>
                            ) : null}
                            <p className="note-label-req">
                                16. Do you have access to guns?
                            </p>
                            <div className="note-radios">
                                <Controller
                                    name={`family.${i}.do_you_have_access_to_guns`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="No"
                                            value="No"
                                            name={`family.${i}.do_you_have_access_to_guns`}
                                            checked={
                                                watch(
                                                    `family.${i}.do_you_have_access_to_guns`
                                                ) === "No"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`family.${i}.do_you_have_access_to_guns`}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioInput
                                            label="Yes"
                                            value="Yes"
                                            name={`family.${i}.do_you_have_access_to_guns`}
                                            checked={
                                                watch(
                                                    `family.${i}.do_you_have_access_to_guns`
                                                ) === "Yes"
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.family?.[i]?.do_you_have_access_to_guns ? (
                                <div className="note-error">
                                    <p>This field is required</p>
                                </div>
                            ) : null}
                            {watch(`family.${i}.do_you_have_access_to_guns`) ===
                            "Yes" ? (
                                <div className="fg">
                                    <TextArea
                                        {...register(
                                            `family.${i}.describe_do_you_have_access_to_guns`
                                        )}
                                        label="Describe the situation"
                                        defaultValue={
                                            noteDetails.note_content?.family?.[
                                                i
                                            ]
                                                ?.describe_do_you_have_access_to_guns
                                        }
                                        hasError={
                                            !!errors.family?.[i]
                                                ?.describe_do_you_have_access_to_guns
                                        }
                                        errorText={
                                            errors.family?.[i]
                                                ?.describe_do_you_have_access_to_guns
                                                ?.message
                                        }
                                    />
                                </div>
                            ) : null}
                        </section>
                    </React.Fragment>
                ))}
                {Array.from({ length: fields.length }, (_, i) => (
                    <React.Fragment key={`${i + 1} key`}>
                        <section id="step-six" className="mb-10">
                            <h3 className="note-header">Mental status exam</h3>
                            <p className="note-header">
                                {i === 0
                                    ? `Client - ${clientName}`
                                    : `Collateral ${i}`}{" "}
                                {i !== 0 && watch(`family.${i}.name`)
                                    ? `- ${
                                          watch(`family.${i}.name`) ||
                                          noteDetails?.note_content.family[i]
                                              ?.name
                                      }`
                                    : ""}
                            </p>
                            <div className="mb-[--spacing-form-items]">
                                <Accordion
                                    buttonPosition="left"
                                    header={
                                        <span className="note-label-req !mb-0">
                                            Appearance
                                        </span>
                                    }
                                    content={
                                        <div className="mt-[--spacing-form-items]">
                                            <div className="note-checkboxes-list !mb-0">
                                                <Controller
                                                    name={`family.${i}.mental_health_appearance`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Neat"
                                                            value="Neat"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_appearance`
                                                                ) || []
                                                            )?.includes("Neat")}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_appearance`
                                                                              ) ||
                                                                                  []),
                                                                              "Neat",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_appearance`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Neat"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`family.${i}.mental_health_appearance`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Unkempt"
                                                            value="Unkempt"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_appearance`
                                                                ) || []
                                                            )?.includes(
                                                                "Unkempt"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_appearance`
                                                                              ) ||
                                                                                  []),
                                                                              "Unkempt",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_appearance`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Unkempt"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_appearance`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Thin"
                                                            value="Thin"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_appearance`
                                                                ) || []
                                                            )?.includes("Thin")}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_appearance`
                                                                              ) ||
                                                                                  []),
                                                                              "Thin",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_appearance`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Thin"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_appearance`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Average"
                                                            value="Average"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_appearance`
                                                                ) || []
                                                            )?.includes(
                                                                "Average"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_appearance`
                                                                              ) ||
                                                                                  []),
                                                                              "Average",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_appearance`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Average"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_appearance`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Overweight"
                                                            value="Overweight"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_appearance`
                                                                ) || []
                                                            )?.includes(
                                                                "Overweight"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_appearance`
                                                                              ) ||
                                                                                  []),
                                                                              "Overweight",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_appearance`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Overweight"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_appearance`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Pale"
                                                            value="Pale"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_appearance`
                                                                ) || []
                                                            )?.includes("Pale")}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_appearance`
                                                                              ) ||
                                                                                  []),
                                                                              "Pale",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_appearance`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Pale"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_appearance`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Tanned"
                                                            value="Tanned"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_appearance`
                                                                ) || []
                                                            )?.includes(
                                                                "Tanned"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_appearance`
                                                                              ) ||
                                                                                  []),
                                                                              "Tanned",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_appearance`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Tanned"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    }
                                />
                            </div>
                            {errors?.family?.[i]?.mental_health_appearance ? (
                                <div className="note-error !mt-0">
                                    <p>Please select at least one option</p>
                                </div>
                            ) : null}
                            <div className="mb-[--spacing-form-items]">
                                <Accordion
                                    buttonPosition="left"
                                    header={
                                        <span className="note-req !mb-0">
                                            Movement
                                        </span>
                                    }
                                    content={
                                        <div className="mt-[--spacing-form-items]">
                                            <div className="note-checkboxes-list !mb-0">
                                                <Controller
                                                    name={`family.${i}.mental_health_movement`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Coordinated"
                                                            value="Coordinated"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_movement`
                                                                ) || []
                                                            )?.includes(
                                                                "Coordinated"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_movement`
                                                                              ) ||
                                                                                  []),
                                                                              "Coordinated",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_movement`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Coordinated"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`family.${i}.mental_health_movement`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Uncoordinated"
                                                            value="Uncoordinated"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_movement`
                                                                ) || []
                                                            )?.includes(
                                                                "Uncoordinated"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_movement`
                                                                              ) ||
                                                                                  []),
                                                                              "Uncoordinated",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_movement`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Uncoordinated"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_movement`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Mannerisms/oddities"
                                                            value="Mannerisms/oddities"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_movement`
                                                                ) || []
                                                            )?.includes(
                                                                "Mannerisms/oddities"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_movement`
                                                                              ) ||
                                                                                  []),
                                                                              "Mannerisms/oddities",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_movement`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Mannerisims/oddities"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_movement`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Good eye contact"
                                                            value="Good eye contact"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_movement`
                                                                ) || []
                                                            )?.includes(
                                                                "Good eye contact"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_movement`
                                                                              ) ||
                                                                                  []),
                                                                              "Good eye contact",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_movement`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Good eye contact"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_movement`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Kept eyes downcast"
                                                            value="Kept eyes downcast"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_movement`
                                                                ) || []
                                                            )?.includes(
                                                                "Kept eyes downcast"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_movement`
                                                                              ) ||
                                                                                  []),
                                                                              "Kept eyes downcast",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_movement`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Kept eyes downcast"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_movement`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Stared into space"
                                                            value="Stared into space"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_movement`
                                                                ) || []
                                                            )?.includes(
                                                                "Stared into space"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_movement`
                                                                              ) ||
                                                                                  []),
                                                                              "Stared into space",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_movement`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Stared into space"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    }
                                />
                            </div>
                            {errors?.family?.[i]?.mental_health_movement ? (
                                <div className="note-error !mt-0">
                                    <p>Please select at least one option</p>
                                </div>
                            ) : null}
                            <div className="mb-[--spacing-form-items]">
                                <Accordion
                                    buttonPosition="left"
                                    header={
                                        <span className="note-label-req !mb-0">
                                            Speech Behavior
                                        </span>
                                    }
                                    content={
                                        <div className="mt-[--spacing-form-items]">
                                            <div className="note-checkboxes-list !mb-0">
                                                <Controller
                                                    name={`family.${i}.mental_health_speech_behaviour`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Unintelligible"
                                                            value="Unintelligible"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_speech_behaviour`
                                                                ) || []
                                                            )?.includes(
                                                                "Unintelligible"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_speech_behaviour`
                                                                              ) ||
                                                                                  []),
                                                                              "Unintelligible",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_speech_behaviour`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Unintelligible"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_speech_behaviour`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Stammer/stutter"
                                                            value="Stammer/stutter"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_speech_behaviour`
                                                                ) || []
                                                            )?.includes(
                                                                "Stammer/stutter"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_speech_behaviour`
                                                                              ) ||
                                                                                  []),
                                                                              "Stammer/stutter",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_speech_behaviour`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Stammer/stutter"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_speech_behaviour`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Mumbles"
                                                            value="Mumbles"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_speech_behaviour`
                                                                ) || []
                                                            )?.includes(
                                                                "Mumbles"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_speech_behaviour`
                                                                              ) ||
                                                                                  []),
                                                                              "Mumbles",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_speech_behaviour`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Mumbles"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_speech_behaviour`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Clear"
                                                            value="Clear"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_speech_behaviour`
                                                                ) || []
                                                            )?.includes(
                                                                "Clear"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_speech_behaviour`
                                                                              ) ||
                                                                                  []),
                                                                              "Clear",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_speech_behaviour`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Clear"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_speech_behaviour`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Precise"
                                                            value="Precise"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_speech_behaviour`
                                                                ) || []
                                                            )?.includes(
                                                                "Precise"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_speech_behaviour`
                                                                              ) ||
                                                                                  []),
                                                                              "Precise",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_speech_behaviour`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Precise"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_speech_behaviour`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Strident"
                                                            value="Strident"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_speech_behaviour`
                                                                ) || []
                                                            )?.includes(
                                                                "Strident"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_speech_behaviour`
                                                                              ) ||
                                                                                  []),
                                                                              "Strident",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_speech_behaviour`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Strident"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_speech_behaviour`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Distant"
                                                            value="Distant"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_speech_behaviour`
                                                                ) || []
                                                            )?.includes(
                                                                "Distant"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_speech_behaviour`
                                                                              ) ||
                                                                                  []),
                                                                              "Distant",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_speech_behaviour`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Distant"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_speech_behaviour`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Squeaky"
                                                            value="Squeaky"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_speech_behaviour`
                                                                ) || []
                                                            )?.includes(
                                                                "Squeaky"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_speech_behaviour`
                                                                              ) ||
                                                                                  []),
                                                                              "Squeaky",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_speech_behaviour`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Squeaky"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_speech_behaviour`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Soft"
                                                            value="Soft"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_speech_behaviour`
                                                                ) || []
                                                            )?.includes("Soft")}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_speech_behaviour`
                                                                              ) ||
                                                                                  []),
                                                                              "Soft",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_speech_behaviour`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Soft"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_speech_behaviour`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Weak"
                                                            value="Weak"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_speech_behaviour`
                                                                ) || []
                                                            )?.includes("Weak")}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_speech_behaviour`
                                                                              ) ||
                                                                                  []),
                                                                              "Weak",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_speech_behaviour`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Weak"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_speech_behaviour`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Normal"
                                                            value="Normal"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_speech_behaviour`
                                                                ) || []
                                                            )?.includes(
                                                                "Normal"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_speech_behaviour`
                                                                              ) ||
                                                                                  []),
                                                                              "Normal",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_speech_behaviour`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Normal"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_speech_behaviour`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Hurried"
                                                            value="Hurried"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_speech_behaviour`
                                                                ) || []
                                                            )?.includes(
                                                                "Hurried"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_speech_behaviour`
                                                                              ) ||
                                                                                  []),
                                                                              "Hurried",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_speech_behaviour`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Hurried"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`family.${i}.mental_health_speech_behaviour`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Loud"
                                                            value="Loud"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_speech_behaviour`
                                                                ) || []
                                                            )?.includes("Loud")}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_speech_behaviour`
                                                                              ) ||
                                                                                  []),
                                                                              "Loud",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_speech_behaviour`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Loud"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    }
                                />
                            </div>
                            {errors?.family?.[i]
                                ?.mental_health_speech_behaviour ? (
                                <div className="note-error !mt-0">
                                    <p>Please select at least one option</p>
                                </div>
                            ) : null}
                            <div className="mb-[--spacing-form-items]">
                                <Accordion
                                    buttonPosition="left"
                                    header={
                                        <span className="note-label-req !mb-0">
                                            Affect/Mood
                                        </span>
                                    }
                                    content={
                                        <div className="mt-[--spacing-form-items]">
                                            <div className="note-checkboxes-list !mb-0">
                                                <Controller
                                                    name={`family.${i}.mental_health_affect_mood`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Blunted"
                                                            value="Blunted"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_affect_mood`
                                                                ) || []
                                                            )?.includes(
                                                                "Blunted"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_affect_mood`
                                                                              ) ||
                                                                                  []),
                                                                              "Blunted",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_affect_mood`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Blunted"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`family.${i}.mental_health_affect_mood`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Constricted"
                                                            value="Constricted"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_affect_mood`
                                                                ) || []
                                                            )?.includes(
                                                                "Constricted"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_affect_mood`
                                                                              ) ||
                                                                                  []),
                                                                              "Constricted",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_affect_mood`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Constricted"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_affect_mood`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Normal"
                                                            value="Normal"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_affect_mood`
                                                                ) || []
                                                            )?.includes(
                                                                "Normal"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_affect_mood`
                                                                              ) ||
                                                                                  []),
                                                                              "Normal",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_affect_mood`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Normal"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_affect_mood`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Broad"
                                                            value="Broad"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_affect_mood`
                                                                ) || []
                                                            )?.includes(
                                                                "Broad"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_affect_mood`
                                                                              ) ||
                                                                                  []),
                                                                              "Broad",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_affect_mood`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Broad"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_affect_mood`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Inappropriate"
                                                            value="Inappropriate"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_affect_mood`
                                                                ) || []
                                                            )?.includes(
                                                                "Inappropriate"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_affect_mood`
                                                                              ) ||
                                                                                  []),
                                                                              "Inappropriate",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_affect_mood`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Inappropriate"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_affect_mood`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Indifferent to problems"
                                                            value="Indifferent to problems"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_affect_mood`
                                                                ) || []
                                                            )?.includes(
                                                                "Indifferent to problems"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_affect_mood`
                                                                              ) ||
                                                                                  []),
                                                                              "Indifferent to problems",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_affect_mood`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Indifferent to problems"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_affect_mood`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Congruent"
                                                            value="Congruent"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_affect_mood`
                                                                ) || []
                                                            )?.includes(
                                                                "Congruent"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_affect_mood`
                                                                              ) ||
                                                                                  []),
                                                                              "Congruent",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_affect_mood`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Congruent"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_affect_mood`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Irritated"
                                                            value="Irritated"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_affect_mood`
                                                                ) || []
                                                            )?.includes(
                                                                "Irritated"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_affect_mood`
                                                                              ) ||
                                                                                  []),
                                                                              "Irritated",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_affect_mood`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Irritated"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_affect_mood`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Hostile"
                                                            value="Hostile"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_affect_mood`
                                                                ) || []
                                                            )?.includes(
                                                                "Hostile"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_affect_mood`
                                                                              ) ||
                                                                                  []),
                                                                              "Hostile",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_affect_mood`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Hostile"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_affect_mood`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Flushing"
                                                            value="flushing"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_affect_mood`
                                                                ) || []
                                                            )?.includes(
                                                                "Flushing"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_affect_mood`
                                                                              ) ||
                                                                                  []),
                                                                              "Flushing",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_affect_mood`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Flushing"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_affect_mood`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Dizziness"
                                                            value="Dizziness"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_affect_mood`
                                                                ) || []
                                                            )?.includes(
                                                                "Dizziness"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_affect_mood`
                                                                              ) ||
                                                                                  []),
                                                                              "Dizziness",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_affect_mood`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Dizziness"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_affect_mood`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Can't sit still"
                                                            value="Can't sit still"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_affect_mood`
                                                                ) || []
                                                            )?.includes(
                                                                "Can't sit still"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_affect_mood`
                                                                              ) ||
                                                                                  []),
                                                                              "Can't sit still",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_affect_mood`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Can't sit still"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_affect_mood`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Panicked"
                                                            value="Panicked"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_affect_mood`
                                                                ) || []
                                                            )?.includes(
                                                                "Panicked"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_affect_mood`
                                                                              ) ||
                                                                                  []),
                                                                              "Panicked",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_affect_mood`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Panicked"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_affect_mood`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Fearful"
                                                            value="Fearful"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_affect_mood`
                                                                ) || []
                                                            )?.includes(
                                                                "Fearful"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_affect_mood`
                                                                              ) ||
                                                                                  []),
                                                                              "Fearful",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_affect_mood`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Fearful"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_affect_mood`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Wretched"
                                                            value="Wretched"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_affect_mood`
                                                                ) || []
                                                            )?.includes(
                                                                "Wretched"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_affect_mood`
                                                                              ) ||
                                                                                  []),
                                                                              "Wretched",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_affect_mood`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Wretched"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_affect_mood`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Melancholy"
                                                            value="Melancholy"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_affect_mood`
                                                                ) || []
                                                            )?.includes(
                                                                "Melancholy"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_affect_mood`
                                                                              ) ||
                                                                                  []),
                                                                              "Melancholy",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_affect_mood`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Melancholy"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_affect_mood`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Sad"
                                                            value="Sad"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_affect_mood`
                                                                ) || []
                                                            )?.includes("Sad")}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_affect_mood`
                                                                              ) ||
                                                                                  []),
                                                                              "Sad",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_affect_mood`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Sad"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_affect_mood`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Hopeless"
                                                            value="Hopeless"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_affect_mood`
                                                                ) || []
                                                            )?.includes(
                                                                "Hopeless"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_affect_mood`
                                                                              ) ||
                                                                                  []),
                                                                              "Hopeless",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_affect_mood`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Hopeless"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_affect_mood`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Bored"
                                                            value="Bored"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_affect_mood`
                                                                ) || []
                                                            )?.includes(
                                                                "Bored"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_affect_mood`
                                                                              ) ||
                                                                                  []),
                                                                              "Bored",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_affect_mood`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Bored"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_affect_mood`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Sorrow"
                                                            value="Sorrow"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_affect_mood`
                                                                ) || []
                                                            )?.includes(
                                                                "Sorrow"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_affect_mood`
                                                                              ) ||
                                                                                  []),
                                                                              "Sorrow",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_affect_mood`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Sorrow"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_affect_mood`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Grief"
                                                            value="Grief"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_affect_mood`
                                                                ) || []
                                                            )?.includes(
                                                                "Grief"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_affect_mood`
                                                                              ) ||
                                                                                  []),
                                                                              "Grief",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_affect_mood`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Grief"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_affect_mood`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Flight of ideas"
                                                            value="Flight of ideas"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_affect_mood`
                                                                ) || []
                                                            )?.includes(
                                                                "Flight of ideas"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_affect_mood`
                                                                              ) ||
                                                                                  []),
                                                                              "Flight of ideas",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_affect_mood`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Flight of ideas"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_affect_mood`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Manic"
                                                            value="Manic"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_affect_mood`
                                                                ) || []
                                                            )?.includes(
                                                                "Manic"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_affect_mood`
                                                                              ) ||
                                                                                  []),
                                                                              "Manic",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_affect_mood`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Manic"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_affect_mood`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Hypomanic"
                                                            value="Hypomanic"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_affect_mood`
                                                                ) || []
                                                            )?.includes(
                                                                "Hypomanic"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_affect_mood`
                                                                              ) ||
                                                                                  []),
                                                                              "Hypomanic",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_affect_mood`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Hypomanic"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_affect_mood`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Ashamed"
                                                            value="Ashamed"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_affect_mood`
                                                                ) || []
                                                            )?.includes(
                                                                "Ashamed"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_affect_mood`
                                                                              ) ||
                                                                                  []),
                                                                              "Ashamed",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_affect_mood`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Ashamed"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_affect_mood`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Embarrassed"
                                                            value="Embarrassed"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_affect_mood`
                                                                ) || []
                                                            )?.includes(
                                                                "Embarrassed"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_affect_mood`
                                                                              ) ||
                                                                                  []),
                                                                              "Embarrassed",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_affect_mood`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Embarrassed"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_affect_mood`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Apologetic"
                                                            value="Apologetic"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_affect_mood`
                                                                ) || []
                                                            )?.includes(
                                                                "Apologetic"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_affect_mood`
                                                                              ) ||
                                                                                  []),
                                                                              "Apologetic",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_affect_mood`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Apologetic"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_affect_mood`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Calm"
                                                            value="Calm"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_affect_mood`
                                                                ) || []
                                                            )?.includes("Calm")}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_affect_mood`
                                                                              ) ||
                                                                                  []),
                                                                              "Calm",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_affect_mood`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Calm"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_affect_mood`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Guilty"
                                                            value="Guilty"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_affect_mood`
                                                                ) || []
                                                            )?.includes(
                                                                "Guilty"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_affect_mood`
                                                                              ) ||
                                                                                  []),
                                                                              "Guilty",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_affect_mood`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Guilty"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`family.${i}.mental_health_affect_mood`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Flat"
                                                            value="Flat"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_affect_mood`
                                                                ) || []
                                                            )?.includes("Flat")}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_affect_mood`
                                                                              ) ||
                                                                                  []),
                                                                              "Flat",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_affect_mood`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Flat"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    }
                                />
                            </div>
                            {errors?.family?.[i]?.mental_health_affect_mood ? (
                                <div className="note-error !mt-0">
                                    <p>Please select at least one option</p>
                                </div>
                            ) : null}
                            <div className="mb-[--spacing-form-items]">
                                <Accordion
                                    buttonPosition="left"
                                    header={
                                        <span className="note-label-req !mb-0">
                                            Attention/Concentration/effort
                                        </span>
                                    }
                                    content={
                                        <div className="mt-[--spacing-form-items]">
                                            <div className="note-checkboxes-list !mb-0">
                                                <Controller
                                                    name={`family.${i}.mental_health_attention_concentration_effort`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Sluggish"
                                                            value="Sluggish"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_attention_concentration_effort`
                                                                ) || []
                                                            )?.includes(
                                                                "Sluggish"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_attention_concentration_effort`
                                                                              ) ||
                                                                                  []),
                                                                              "Sluggish",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_attention_concentration_effort`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Sluggish"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`family.${i}.mental_health_attention_concentration_effort`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Flat"
                                                            value="Flat"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_attention_concentration_effort`
                                                                ) || []
                                                            )?.includes("Flat")}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_attention_concentration_effort`
                                                                              ) ||
                                                                                  []),
                                                                              "Flat",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_attention_concentration_effort`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Flat"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_attention_concentration_effort`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Distractible"
                                                            value="Distractible"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_attention_concentration_effort`
                                                                ) || []
                                                            )?.includes(
                                                                "Distractible"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_attention_concentration_effort`
                                                                              ) ||
                                                                                  []),
                                                                              "Distractible",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_attention_concentration_effort`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Distractible"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_attention_concentration_effort`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Normal energy"
                                                            value="Normal energy"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_attention_concentration_effort`
                                                                ) || []
                                                            )?.includes(
                                                                "Normal energy"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_attention_concentration_effort`
                                                                              ) ||
                                                                                  []),
                                                                              "Normal energy",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_attention_concentration_effort`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Normal energy"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_attention_concentration_effort`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Eager"
                                                            value="Eager"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_attention_concentration_effort`
                                                                ) || []
                                                            )?.includes(
                                                                "Eager"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_attention_concentration_effort`
                                                                              ) ||
                                                                                  []),
                                                                              "Eager",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_attention_concentration_effort`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Eager"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_attention_concentration_effort`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Indifferent"
                                                            value="Indifferent"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_attention_concentration_effort`
                                                                ) || []
                                                            )?.includes(
                                                                "Indifferent"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_attention_concentration_effort`
                                                                              ) ||
                                                                                  []),
                                                                              "Indifferent",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_attention_concentration_effort`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Indifferent"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_attention_concentration_effort`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Scattered"
                                                            value="Scattered"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_attention_concentration_effort`
                                                                ) || []
                                                            )?.includes(
                                                                "Scattered"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_attention_concentration_effort`
                                                                              ) ||
                                                                                  []),
                                                                              "Scattered",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_attention_concentration_effort`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Scattered"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_attention_concentration_effort`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Baffled"
                                                            value="Baffled"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_attention_concentration_effort`
                                                                ) || []
                                                            )?.includes(
                                                                "Baffled"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_attention_concentration_effort`
                                                                              ) ||
                                                                                  []),
                                                                              "Baffled",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_attention_concentration_effort`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Baffled"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_attention_concentration_effort`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Perplexed"
                                                            value="Perplexed"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_attention_concentration_effort`
                                                                ) || []
                                                            )?.includes(
                                                                "Perplexed"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_attention_concentration_effort`
                                                                              ) ||
                                                                                  []),
                                                                              "Perplexed",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_attention_concentration_effort`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Perplexed"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_attention_concentration_effort`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Hurried"
                                                            value="Hurried"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_attention_concentration_effort`
                                                                ) || []
                                                            )?.includes(
                                                                "Hurried"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_attention_concentration_effort`
                                                                              ) ||
                                                                                  []),
                                                                              "Hurried",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_attention_concentration_effort`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Hurried"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_attention_concentration_effort`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Organized"
                                                            value="Organized"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_attention_concentration_effort`
                                                                ) || []
                                                            )?.includes(
                                                                "Organized"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_attention_concentration_effort`
                                                                              ) ||
                                                                                  []),
                                                                              "Organized",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_attention_concentration_effort`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Organized"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_attention_concentration_effort`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Rigid"
                                                            value="Rigid"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_attention_concentration_effort`
                                                                ) || []
                                                            )?.includes(
                                                                "Rigid"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_attention_concentration_effort`
                                                                              ) ||
                                                                                  []),
                                                                              "Rigid",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_attention_concentration_effort`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Rigid"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_attention_concentration_effort`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Pleasant"
                                                            value="Pleasant"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_attention_concentration_effort`
                                                                ) || []
                                                            )?.includes(
                                                                "Pleasant"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_attention_concentration_effort`
                                                                              ) ||
                                                                                  []),
                                                                              "Pleasant",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_attention_concentration_effort`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Pleasant"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_attention_concentration_effort`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Cooperative"
                                                            value="Cooperative"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_attention_concentration_effort`
                                                                ) || []
                                                            )?.includes(
                                                                "Cooperative"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_attention_concentration_effort`
                                                                              ) ||
                                                                                  []),
                                                                              "Cooperative",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_attention_concentration_effort`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Cooperative"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_attention_concentration_effort`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Dependent"
                                                            value="Dependent"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_attention_concentration_effort`
                                                                ) || []
                                                            )?.includes(
                                                                "Dependent"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_attention_concentration_effort`
                                                                              ) ||
                                                                                  []),
                                                                              "Dependent",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_attention_concentration_effort`
                                                                          ).filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Dependent"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_attention_concentration_effort`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Abusive"
                                                            value="Abusive"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_attention_concentration_effort`
                                                                ) || []
                                                            )?.includes(
                                                                "Abusive"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_attention_concentration_effort`
                                                                              ) ||
                                                                                  []),
                                                                              "Abusive",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_attention_concentration_effort`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Abusive"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_attention_concentration_effort`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Superior"
                                                            value="Superior"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_attention_concentration_effort`
                                                                ) || []
                                                            )?.includes(
                                                                "Superior"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_attention_concentration_effort`
                                                                              ) ||
                                                                                  []),
                                                                              "Superior",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_attention_concentration_effort`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Superior"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_attention_concentration_effort`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Stubborn"
                                                            value="Stubborn"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_attention_concentration_effort`
                                                                ) || []
                                                            )?.includes(
                                                                "Stubborn"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_attention_concentration_effort`
                                                                              ) ||
                                                                                  []),
                                                                              "Stubborn",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_attention_concentration_effort`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Stubborn"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_attention_concentration_effort`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Belligerent"
                                                            value="Belligerent"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_attention_concentration_effort`
                                                                ) || []
                                                            )?.includes(
                                                                "Belligerent"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_attention_concentration_effort`
                                                                              ) ||
                                                                                  []),
                                                                              "Belligerent",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_attention_concentration_effort`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Belligerent"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_attention_concentration_effort`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Argumentative"
                                                            value="Argumentative"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_attention_concentration_effort`
                                                                ) || []
                                                            )?.includes(
                                                                "Argumentative"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_attention_concentration_effort`
                                                                              ) ||
                                                                                  []),
                                                                              "Argumentative",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_attention_concentration_effort`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Argumentative"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_attention_concentration_effort`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Hostile"
                                                            value="Hostile"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_attention_concentration_effort`
                                                                ) || []
                                                            )?.includes(
                                                                "Hostile"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_attention_concentration_effort`
                                                                              ) ||
                                                                                  []),
                                                                              "Hostile",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_attention_concentration_effort`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Hostile"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_attention_concentration_effort`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Demanding"
                                                            value="Demanding"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_attention_concentration_effort`
                                                                ) || []
                                                            )?.includes(
                                                                "Demanding"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_attention_concentration_effort`
                                                                              ) ||
                                                                                  []),
                                                                              "Demanding",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_attention_concentration_effort`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Demanding"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_attention_concentration_effort`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Resentful"
                                                            value="Resentful"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_attention_concentration_effort`
                                                                ) || []
                                                            )?.includes(
                                                                "Resentful"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_attention_concentration_effort`
                                                                              ) ||
                                                                                  []),
                                                                              "Resentful",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_attention_concentration_effort`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Resentful"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_attention_concentration_effort`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Surly"
                                                            value="Surly"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_attention_concentration_effort`
                                                                ) || []
                                                            )?.includes(
                                                                "Surly"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_attention_concentration_effort`
                                                                              ) ||
                                                                                  []),
                                                                              "Surly",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_attention_concentration_effort`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Surly"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_attention_concentration_effort`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Guarded"
                                                            value="Guarded"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_attention_concentration_effort`
                                                                ) || []
                                                            )?.includes(
                                                                "Guarded"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_attention_concentration_effort`
                                                                              ) ||
                                                                                  []),
                                                                              "Guarded",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_attention_concentration_effort`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Guarded"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    }
                                />
                            </div>
                            {errors?.family?.[i]
                                ?.mental_health_attention_concentration_effort ? (
                                <div className="note-error !mt-0">
                                    <p>Please select at least one option</p>
                                </div>
                            ) : null}
                            <div className="mb-[--spacing-form-items]">
                                <Accordion
                                    buttonPosition="left"
                                    header={
                                        <span className="note-label-req !mb-0">
                                            Presentation of self
                                        </span>
                                    }
                                    content={
                                        <div className="mt-[--spacing-form-items]">
                                            <div className="note-checkboxes-list !mb-0">
                                                <Controller
                                                    name={`family.${i}.mental_health_presentation_of_self`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Withdrawn"
                                                            value="Withdrawn"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_presentation_of_self`
                                                                ) || []
                                                            )?.includes(
                                                                "Withdrawn"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_presentation_of_self`
                                                                              ) ||
                                                                                  []),
                                                                              "Withdrawn",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_presentation_of_self`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Withdrawn"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`family.${i}.mental_health_presentation_of_self`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Threatened"
                                                            value="Threatened"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_presentation_of_self`
                                                                ) || []
                                                            )?.includes(
                                                                "Threatened"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_presentation_of_self`
                                                                              ) ||
                                                                                  []),
                                                                              "Threatened",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_presentation_of_self`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Threatened"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_presentation_of_self`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Vulnerable"
                                                            value="Vulnerable"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_presentation_of_self`
                                                                ) || []
                                                            )?.includes(
                                                                "Vulnerable"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_presentation_of_self`
                                                                              ) ||
                                                                                  []),
                                                                              "Vulnerable",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_presentation_of_self`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Vunlnerable"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_presentation_of_self`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Shy"
                                                            value="Shy"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_presentation_of_self`
                                                                ) || []
                                                            )?.includes("Shy")}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_presentation_of_self`
                                                                              ) ||
                                                                                  []),
                                                                              "Shy",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_presentation_of_self`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Shy"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_presentation_of_self`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Friendly"
                                                            value="Friendly"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_presentation_of_self`
                                                                ) || []
                                                            )?.includes(
                                                                "Friendly"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_presentation_of_self`
                                                                              ) ||
                                                                                  []),
                                                                              "Friendly",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_presentation_of_self`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Friendly"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_presentation_of_self`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Confident"
                                                            value="Confident"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_presentation_of_self`
                                                                ) || []
                                                            )?.includes(
                                                                "Confident"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_presentation_of_self`
                                                                              ) ||
                                                                                  []),
                                                                              "Confident",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_presentation_of_self`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Confident"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_presentation_of_self`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Grandiose"
                                                            value="Grandiose"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_presentation_of_self`
                                                                ) || []
                                                            )?.includes(
                                                                "Grandiose"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_presentation_of_self`
                                                                              ) ||
                                                                                  []),
                                                                              "Grandiose",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_presentation_of_self`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Grandiose"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_presentation_of_self`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Humble"
                                                            value="Humble"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_presentation_of_self`
                                                                ) || []
                                                            )?.includes(
                                                                "Humble"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_presentation_of_self`
                                                                              ) ||
                                                                                  []),
                                                                              "Humble",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_presentation_of_self`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Humble"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_presentation_of_self`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Self-doubting"
                                                            value="Self-doubting"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_presentation_of_self`
                                                                ) || []
                                                            )?.includes(
                                                                "Self-doubting"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_presentation_of_self`
                                                                              ) ||
                                                                                  []),
                                                                              "Self-doubting",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_presentation_of_self`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Self-doubting"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_presentation_of_self`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Hopeful"
                                                            value="Hopeful"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_presentation_of_self`
                                                                ) || []
                                                            )?.includes(
                                                                "Hopeful"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_presentation_of_self`
                                                                              ) ||
                                                                                  []),
                                                                              "Hopeful",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_presentation_of_self`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Hopeful"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_presentation_of_self`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Pessimistic"
                                                            value="Pessimistic"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_presentation_of_self`
                                                                ) || []
                                                            )?.includes(
                                                                "Pessimistic"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_presentation_of_self`
                                                                              ) ||
                                                                                  []),
                                                                              "Pessimistic",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_presentation_of_self`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Pessimistic"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_presentation_of_self`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Overindulgent"
                                                            value="Overindulgent"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_presentation_of_self`
                                                                ) || []
                                                            )?.includes(
                                                                "Overindulgent"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_presentation_of_self`
                                                                              ) ||
                                                                                  []),
                                                                              "Overindulgent",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_presentation_of_self`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Overindulgent"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_presentation_of_self`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Warm hearted"
                                                            value="Warm hearted"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_presentation_of_self`
                                                                ) || []
                                                            )?.includes(
                                                                "Warm hearted"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_presentation_of_self`
                                                                              ) ||
                                                                                  []),
                                                                              "Warm hearted",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_presentation_of_self`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Warm hearted"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_presentation_of_self`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Distant"
                                                            value="Distant"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_presentation_of_self`
                                                                ) || []
                                                            )?.includes(
                                                                "Distant"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_presentation_of_self`
                                                                              ) ||
                                                                                  []),
                                                                              "Distant",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_presentation_of_self`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Distant"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />

                                                <Controller
                                                    name={`family.${i}.mental_health_presentation_of_self`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Harsh"
                                                            value="Harsh"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_presentation_of_self`
                                                                ) || []
                                                            )?.includes(
                                                                "Harsh"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_presentation_of_self`
                                                                              ) ||
                                                                                  []),
                                                                              "Harsh",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_presentation_of_self`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Harsh"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    }
                                />
                            </div>
                            {errors?.family?.[i]
                                ?.mental_health_presentation_of_self ? (
                                <div className="note-error !mt-0">
                                    <p>Please select at least one option</p>
                                </div>
                            ) : null}
                            <div className="mb-[--spacing-form-items]">
                                <Accordion
                                    buttonPosition="left"
                                    header={
                                        <span className="note-label-req !mb-0">
                                            Cognition (decision making, insight,
                                            memory, etc)
                                        </span>
                                    }
                                    content={
                                        <div className="mt-[--spacing-form-items]">
                                            <div className="note-checkboxes-list-group">
                                                <Controller
                                                    name={`family.${i}.mental_health_cognition`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="No insight"
                                                            value="No insight"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_cognition`
                                                                ) || []
                                                            )?.includes(
                                                                "No insight"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_cognition`
                                                                              ) ||
                                                                                  []),
                                                                              "No insight",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_cognition`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "No insight"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`family.${i}.mental_health_cognition`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Some insight"
                                                            value="Some insight"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_cognition`
                                                                ) || []
                                                            )?.includes(
                                                                "Some insight"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_cognition`
                                                                              ) ||
                                                                                  []),
                                                                              "Some insight",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_cognition`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Some insight"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`family.${i}.mental_health_cognition`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Full insight"
                                                            value="Full insight"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_cognition`
                                                                ) || []
                                                            )?.includes(
                                                                "Full insight"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_cognition`
                                                                              ) ||
                                                                                  []),
                                                                              "Full insight",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_cognition`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Full insight"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </div>
                                            <div className="note-checkboxes-list-group">
                                                <Controller
                                                    name={`family.${i}.mental_health_cognition`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Sluggish recall"
                                                            value="Sluggish recall"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_cognition`
                                                                ) || []
                                                            )?.includes(
                                                                "Sluggish recall"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_cognition`
                                                                              ) ||
                                                                                  []),
                                                                              "Sluggish recall",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_cognition`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Sluggish recall"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`family.${i}.mental_health_cognition`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Low intelligent"
                                                            value="Low intelligent"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_cognition`
                                                                ) || []
                                                            )?.includes(
                                                                "Low intelligent"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_cognition`
                                                                              ) ||
                                                                                  []),
                                                                              "Low intelligent",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_cognition`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Low intelligient"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`family.${i}.mental_health_cognition`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Intelligent"
                                                            value="Intelligent"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_cognition`
                                                                ) || []
                                                            )?.includes(
                                                                "Intelligent"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_cognition`
                                                                              ) ||
                                                                                  []),
                                                                              "Intelligent",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_cognition`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Intelligent"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </div>
                                            <div className="note-checkboxes-list-group">
                                                <Controller
                                                    name={`family.${i}.mental_health_cognition`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Defective reality"
                                                            value="Defective reality"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_cognition`
                                                                ) || []
                                                            )?.includes(
                                                                "Defective reality"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_cognition`
                                                                              ) ||
                                                                                  []),
                                                                              "Defective reality",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_cognition`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Defective reality"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`family.${i}.mental_health_cognition`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Impoverished stream of thoughts"
                                                            value="Impoverished stream of thoughts"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_cognition`
                                                                ) || []
                                                            )?.includes(
                                                                "Impoverished stream of thoughts"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_cognition`
                                                                              ) ||
                                                                                  []),
                                                                              "Impoverished stream of thoughts",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_cognition`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Impoverished stream of thoughts"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`family.${i}.mental_health_cognition`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Normal stream of thoughts"
                                                            value="Normal stream of thoughts"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_cognition`
                                                                ) || []
                                                            )?.includes(
                                                                "Normal stream of thoughts"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_cognition`
                                                                              ) ||
                                                                                  []),
                                                                              "Normal stream of thoughts",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_cognition`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Normal stream of thoughts"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </div>
                                            <div className="note-checkboxes-list-group">
                                                <Controller
                                                    name={`family.${i}.mental_health_cognition`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Incoherent thought process"
                                                            value="Incoherent thought process"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_cognition`
                                                                ) || []
                                                            )?.includes(
                                                                "Incoherent thought process"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_cognition`
                                                                              ) ||
                                                                                  []),
                                                                              "Incoherent thought process",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_cognition`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Incoherent thought process"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`family.${i}.mental_health_cognition`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Loose thought process"
                                                            value="Loose thought process"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_cognition`
                                                                ) || []
                                                            )?.includes(
                                                                "Loose thought process"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_cognition`
                                                                              ) ||
                                                                                  []),
                                                                              "Loose thought process",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_cognition`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Loose thought process"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`family.${i}.mental_health_cognition`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Clear thought process"
                                                            value="Clear thought process"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_cognition`
                                                                ) || []
                                                            )?.includes(
                                                                "Clear thought process"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_cognition`
                                                                              ) ||
                                                                                  []),
                                                                              "Clear thought process",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_cognition`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Clear thought process"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </div>
                                            <div className="note-checkboxes-list-group">
                                                <Controller
                                                    name={`family.${i}.mental_health_cognition`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Flight of ideas"
                                                            value="Flight of ideas"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_cognition`
                                                                ) || []
                                                            )?.includes(
                                                                "Flight of ideas"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_cognition`
                                                                              ) ||
                                                                                  []),
                                                                              "Flight of ideas",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_cognition`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Flight of ideas"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`family.${i}.mental_health_cognition`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Indecisive"
                                                            value="Indecisive"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_cognition`
                                                                ) || []
                                                            )?.includes(
                                                                "Indecisive"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_cognition`
                                                                              ) ||
                                                                                  []),
                                                                              "Indecisive",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_cognition`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Indecisive"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`family.${i}.mental_health_cognition`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Decisive"
                                                            value="Decisive"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_cognition`
                                                                ) || []
                                                            )?.includes(
                                                                "Decisive"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_cognition`
                                                                              ) ||
                                                                                  []),
                                                                              "Decisive",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_cognition`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Decisive"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </div>
                                            <div className="note-checkboxes-list-group">
                                                <Controller
                                                    name={`family.${i}.mental_health_cognition`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Lack of common sense"
                                                            value="Lack of common sense"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_cognition`
                                                                ) || []
                                                            )?.includes(
                                                                "Lack of common sense"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_cognition`
                                                                              ) ||
                                                                                  []),
                                                                              "Lack of common sense",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_cognition`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Lack of common sense"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`family.${i}.mental_health_cognition`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Forgetful"
                                                            value="Forgetful"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_cognition`
                                                                ) || []
                                                            )?.includes(
                                                                "Forgetful"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_cognition`
                                                                              ) ||
                                                                                  []),
                                                                              "Forgetful",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_cognition`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Forgetful"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`family.${i}.mental_health_cognition`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Follows through"
                                                            value="Follows through"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_cognition`
                                                                ) || []
                                                            )?.includes(
                                                                "Follows through"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_cognition`
                                                                              ) ||
                                                                                  []),
                                                                              "Follows through",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_cognition`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Follows through"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </div>
                                            <div className="note-checkboxes-list-group">
                                                <Controller
                                                    name={`family.${i}.mental_health_cognition`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Faulty reasoning"
                                                            value="Faulty reasoning"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_cognition`
                                                                ) || []
                                                            )?.includes(
                                                                "Faulty reasoning"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_cognition`
                                                                              ) ||
                                                                                  []),
                                                                              "Faulty reasoning",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_cognition`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Faulty reasoning"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`family.${i}.mental_health_cognition`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Easily confused"
                                                            value="Easily confused"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_cognition`
                                                                ) || []
                                                            )?.includes(
                                                                "Easily confused"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_cognition`
                                                                              ) ||
                                                                                  []),
                                                                              "Easily confused",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_cognition`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Easily confused"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`family.${i}.mental_health_cognition`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Normal abstraction"
                                                            value="Normal abstraction"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_cognition`
                                                                ) || []
                                                            )?.includes(
                                                                "Normal abstraction"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_cognition`
                                                                              ) ||
                                                                                  []),
                                                                              "Normal abstraction",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_cognition`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Normal abstraction"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </div>
                                            <div className="note-checkboxes-list-group">
                                                <Controller
                                                    name={`family.${i}.mental_health_cognition`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Procrastinates"
                                                            value="Procrastinates"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_cognition`
                                                                ) || []
                                                            )?.includes(
                                                                "Procrastinates"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_cognition`
                                                                              ) ||
                                                                                  []),
                                                                              "Procrastinates",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_cognition`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Procrastinates"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`family.${i}.mental_health_cognition`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Confuses time frames/sequence"
                                                            value="Confuses time frames/sequence"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_cognition`
                                                                ) || []
                                                            )?.includes(
                                                                "Confuses time frames/sequence"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_cognition`
                                                                              ) ||
                                                                                  []),
                                                                              "Confuses time frames/sequence",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_cognition`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Confuses time frames/sequence"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`family.${i}.mental_health_cognition`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Normal memory"
                                                            value="Normal memory"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_cognition`
                                                                ) || []
                                                            )?.includes(
                                                                "Normal memory"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_cognition`
                                                                              ) ||
                                                                                  []),
                                                                              "Normal memory",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_cognition`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Normal memory"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </div>
                                            <div className="note-checkboxes-list-group">
                                                <Controller
                                                    name={`family.${i}.mental_health_cognition`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Amnesia"
                                                            value="Amnesia"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_cognition`
                                                                ) || []
                                                            )?.includes(
                                                                "Amnesia"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_cognition`
                                                                              ) ||
                                                                                  []),
                                                                              "Amnesia",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_cognition`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Amnesia"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`family.${i}.mental_health_cognition`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Confused present location/date/persons, places"
                                                            value="Confused present location/date/persons, places"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_cognition`
                                                                ) || []
                                                            )?.includes(
                                                                "Confused present location/date/persons, places"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_cognition`
                                                                              ) ||
                                                                                  []),
                                                                              "Confused present location/date/persons, places",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_cognition`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Confused present location/date/persons, places"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`family.${i}.mental_health_cognition`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Good orientation"
                                                            value="Good orientation"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_cognition`
                                                                ) || []
                                                            )?.includes(
                                                                "Good orientation"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_cognition`
                                                                              ) ||
                                                                                  []),
                                                                              "Good orientation",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_cognition`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Good orientation"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </div>
                                            <div className="note-checkboxes-list-group">
                                                <div />
                                                <Controller
                                                    name={`family.${i}.mental_health_cognition`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Victimization"
                                                            value="Victimization"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_cognition`
                                                                ) || []
                                                            )?.includes(
                                                                "Victimization"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_cognition`
                                                                              ) ||
                                                                                  []),
                                                                              "Victimization",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_cognition`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Victimization"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`family.${i}.mental_health_cognition`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Normal judgement"
                                                            value="Normal judgment"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_cognition`
                                                                ) || []
                                                            )?.includes(
                                                                "Normal judgment"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_cognition`
                                                                              ) ||
                                                                                  []),
                                                                              "Normal judgment",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_cognition`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Normal judgment"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </div>
                                            <div className="note-checkboxes-list-group">
                                                <div />
                                                <div />
                                                <Controller
                                                    name={`family.${i}.mental_health_cognition`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormCheckbox
                                                            label="Intact reality"
                                                            value="Intact reality"
                                                            checked={(
                                                                watch(
                                                                    `family.${i}.mental_health_cognition`
                                                                ) || []
                                                            )?.includes(
                                                                "Intact reality"
                                                            )}
                                                            onChange={(e) => {
                                                                const updatedValue =
                                                                    e.target
                                                                        .checked
                                                                        ? [
                                                                              ...(watch(
                                                                                  `family.${i}.mental_health_cognition`
                                                                              ) ||
                                                                                  []),
                                                                              "Intact reality",
                                                                          ]
                                                                        : watch(
                                                                              `family.${i}.mental_health_cognition`
                                                                          )?.filter(
                                                                              (
                                                                                  symptom
                                                                              ) =>
                                                                                  symptom !==
                                                                                  "Intact reality"
                                                                          );
                                                                field.onChange(
                                                                    updatedValue
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    }
                                />
                            </div>
                            {errors?.family?.[i]?.mental_health_cognition ? (
                                <div className="note-error !mt-0">
                                    <p>Please select at least one option</p>
                                </div>
                            ) : null}

                            <div>
                                <h3 className="note-label-req">
                                    Patient&apos;s ability and capability to
                                    respond to treatment
                                </h3>
                                <div className="note-checkboxes">
                                    <Controller
                                        name={`family.${i}.patients_ability_and_capability_to_respond_to_treatment`}
                                        control={control}
                                        render={({ field }) => (
                                            <FormCheckbox
                                                label="Consistent, good-quality patient-oriented evidence"
                                                value="Consistent, good-quality patient-oriented evidence"
                                                checked={(
                                                    watch(
                                                        `family.${i}.patients_ability_and_capability_to_respond_to_treatment`
                                                    ) || []
                                                )?.includes(
                                                    "Consistent, good-quality patient-oriented evidence"
                                                )}
                                                onChange={(e) => {
                                                    const updatedValue = e
                                                        .target.checked
                                                        ? [
                                                              ...(watch(
                                                                  `family.${i}.patients_ability_and_capability_to_respond_to_treatment`
                                                              ) || []),
                                                              "Consistent, good-quality patient-oriented evidence",
                                                          ]
                                                        : watch(
                                                              `family.${i}.patients_ability_and_capability_to_respond_to_treatment`
                                                          )?.filter(
                                                              (symptom) =>
                                                                  symptom !==
                                                                  "Consistent, good-quality patient-oriented evidence"
                                                          );
                                                    field.onChange(
                                                        updatedValue
                                                    );
                                                }}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name={`family.${i}.patients_ability_and_capability_to_respond_to_treatment`}
                                        control={control}
                                        render={({ field }) => (
                                            <FormCheckbox
                                                label="Diminished/poor or limited-quality patient-oriented evidence"
                                                value="Diminished/poor or limited-quality patient-oriented evidence"
                                                checked={(
                                                    watch(
                                                        `family.${i}.patients_ability_and_capability_to_respond_to_treatment`
                                                    ) || []
                                                )?.includes(
                                                    "Diminished/poor or limited-quality patient-oriented evidence"
                                                )}
                                                onChange={(e) => {
                                                    const updatedValue = e
                                                        .target.checked
                                                        ? [
                                                              ...(watch(
                                                                  `family.${i}.patients_ability_and_capability_to_respond_to_treatment`
                                                              ) || []),
                                                              "Diminished/poor or limited-quality patient-oriented evidence",
                                                          ]
                                                        : watch(
                                                              `family.${i}.patients_ability_and_capability_to_respond_to_treatment`
                                                          )?.filter(
                                                              (symptom) =>
                                                                  symptom !==
                                                                  "Diminished/poor or limited-quality patient-oriented evidence"
                                                          );
                                                    field.onChange(
                                                        updatedValue
                                                    );
                                                }}
                                            />
                                        )}
                                    />

                                    {(
                                        watch(
                                            `family.${i}.patients_ability_and_capability_to_respond_to_treatment`
                                        ) || []
                                    )?.includes(
                                        "Diminished/poor or limited-quality patient-oriented evidence"
                                    ) && (
                                        <>
                                            <span className="note-label !mb-0">
                                                Please provide details for
                                                diminished/poor quality
                                                patient-orientation
                                            </span>
                                            <TextArea
                                                {...register(
                                                    `family.${i}.details_for_diminished_poor_limited_quality_patient_oriented_evidence`
                                                )}
                                                defaultValue={watch(
                                                    `family.${i}.details_for_diminished_poor_limited_quality_patient_oriented_evidence`
                                                )}
                                                hasError={
                                                    !!errors.family?.[i]
                                                        ?.details_for_diminished_poor_limited_quality_patient_oriented_evidence
                                                }
                                                errorText={
                                                    errors.family?.[i]
                                                        ?.details_for_diminished_poor_limited_quality_patient_oriented_evidence
                                                        ?.message
                                                }
                                            />
                                        </>
                                    )}
                                </div>
                                {errors.family?.[i]
                                    ?.patients_ability_and_capability_to_respond_to_treatment ? (
                                    <div className="note-error">
                                        <p>This field is required</p>
                                    </div>
                                ) : null}
                            </div>
                        </section>
                    </React.Fragment>
                ))}
                <section id="step-seven" className="mb-10">
                    <h3 className="note-header">Summary</h3>
                    <p className="note-label-req">
                        {
                            FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.people_present_at_session
                        }
                    </p>
                    <div className="note-checkboxes-list">
                        <FormCheckbox
                            {...register(`people_present_at_session`)}
                            label="Patient"
                            value="Patient"
                            defaultChecked={isChecked(
                                "Patient",
                                noteDetails.note_content
                                    ?.people_present_at_session
                            )}
                        />
                        <FormCheckbox
                            {...register(`people_present_at_session`)}
                            label="Mother"
                            value="Mother"
                            defaultChecked={isChecked(
                                "Mother",
                                noteDetails.note_content
                                    ?.people_present_at_session
                            )}
                        />
                        <FormCheckbox
                            {...register(`people_present_at_session`)}
                            label="Stepfather"
                            value="Stepfather"
                            defaultChecked={isChecked(
                                "Stepfather",
                                noteDetails.note_content
                                    ?.people_present_at_session
                            )}
                        />
                        <FormCheckbox
                            {...register(`people_present_at_session`)}
                            label="Spouse"
                            value="Spouse"
                            defaultChecked={isChecked(
                                "Spouse",
                                noteDetails.note_content
                                    ?.people_present_at_session
                            )}
                        />
                        <FormCheckbox
                            {...register(`people_present_at_session`)}
                            label="Father"
                            value="Father"
                            defaultChecked={isChecked(
                                "Father",
                                noteDetails.note_content
                                    ?.people_present_at_session
                            )}
                        />
                        <FormCheckbox
                            {...register(`people_present_at_session`)}
                            label="Stepmother"
                            value="Stepmother"
                            defaultChecked={isChecked(
                                "Stepmother",
                                noteDetails.note_content
                                    ?.people_present_at_session
                            )}
                        />
                        <FormCheckbox
                            {...register(`people_present_at_session`)}
                            label="Partner"
                            value="Partner"
                            defaultChecked={isChecked(
                                "Partner",
                                noteDetails.note_content
                                    ?.people_present_at_session
                            )}
                        />
                        <FormCheckbox
                            {...register(`people_present_at_session`)}
                            label="Guardian"
                            value="Guardian"
                            defaultChecked={isChecked(
                                "Guardian",
                                noteDetails.note_content
                                    ?.people_present_at_session
                            )}
                        />
                        <FormCheckbox
                            {...register(`people_present_at_session`)}
                            label="Other"
                            value="Other"
                            defaultChecked={isChecked(
                                "Other",
                                noteDetails.note_content
                                    ?.people_present_at_session
                            )}
                        />
                    </div>
                    {errors.people_present_at_session ? (
                        <div className="note-error">
                            <p>People present at session is required</p>
                        </div>
                    ) : null}
                    <div className="fg">
                        <Input
                            {...register(`other_person_present_at_session`)}
                            label={
                                FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.other_person_present_at_session
                            }
                            defaultValue={
                                noteDetails.note_content
                                    ?.other_person_present_at_session
                            }
                            isOptional
                        />
                    </div>
                    <p className="note-label-req">
                        {
                            FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.type_of_treatment_recommended
                        }
                    </p>
                    <div className="note-checkboxes-list">
                        <FormCheckbox
                            {...register(`type_of_treatment_recommended`)}
                            label="Individual"
                            value="Individual"
                            defaultChecked={isChecked(
                                "Individual",
                                noteDetails.note_content
                                    ?.type_of_treatment_recommended
                            )}
                        />
                        <FormCheckbox
                            {...register(`type_of_treatment_recommended`)}
                            label="Family"
                            value="Family"
                            defaultChecked={isChecked(
                                "Family",
                                noteDetails.note_content
                                    ?.type_of_treatment_recommended
                            )}
                        />
                        <FormCheckbox
                            {...register(`type_of_treatment_recommended`)}
                            label="Group"
                            value="Group"
                            defaultChecked={isChecked(
                                "Group",
                                noteDetails.note_content
                                    ?.type_of_treatment_recommended
                            )}
                        />
                        <FormCheckbox
                            {...register(`type_of_treatment_recommended`)}
                            label="Collateral/Parenting"
                            value="Collateral/Parenting"
                            defaultChecked={isChecked(
                                "Collateral/Parenting",
                                noteDetails.note_content
                                    ?.type_of_treatment_recommended
                            )}
                        />
                    </div>
                    {errors.type_of_treatment_recommended ? (
                        <div className="note-error">
                            <p>Type of treatment recommended is required</p>
                        </div>
                    ) : null}
                    <p className="note-label-req">
                        {
                            FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.treatment_modality_intervention
                        }
                    </p>
                    <div className="note-checkboxes-list">
                        <FormCheckbox
                            {...register(`treatment_modality_intervention`)}
                            label="Cognitive/Behavioral"
                            value="Cognitive/Behavioral"
                            defaultChecked={isChecked(
                                "Cognitive/Behavioral",
                                noteDetails.note_content
                                    ?.treatment_modality_intervention
                            )}
                        />
                        <FormCheckbox
                            {...register(`treatment_modality_intervention`)}
                            label="Behavioral Modification"
                            value="Behavioral Modification"
                            defaultChecked={isChecked(
                                "Behavioral Modification",
                                noteDetails.note_content
                                    ?.treatment_modality_intervention
                            )}
                        />
                        <FormCheckbox
                            {...register(`treatment_modality_intervention`)}
                            label="Supportive"
                            value="Supportive"
                            defaultChecked={isChecked(
                                "Supportive",
                                noteDetails.note_content
                                    ?.treatment_modality_intervention
                            )}
                        />
                        <FormCheckbox
                            {...register(`treatment_modality_intervention`)}
                            label="Marital/Couples Therapy "
                            value="Marital/Couples Therapy "
                            defaultChecked={isChecked(
                                "Marital/Couples Therapy ",
                                noteDetails.note_content
                                    ?.treatment_modality_intervention
                            )}
                        />
                        <FormCheckbox
                            {...register(`treatment_modality_intervention`)}
                            label="Family Therapy"
                            value="Family Therapy"
                            defaultChecked={isChecked(
                                "Family Therapy",
                                noteDetails.note_content
                                    ?.treatment_modality_intervention
                            )}
                        />
                        <FormCheckbox
                            {...register(`treatment_modality_intervention`)}
                            label="Stress Management"
                            value="Stress Management"
                            defaultChecked={isChecked(
                                "Stress Management",
                                noteDetails.note_content
                                    ?.treatment_modality_intervention
                            )}
                        />
                        <FormCheckbox
                            {...register(`treatment_modality_intervention`)}
                            label="Psychodynamic"
                            value="Psychodynamic"
                            defaultChecked={isChecked(
                                "Psychodynamic",
                                noteDetails.note_content
                                    ?.treatment_modality_intervention
                            )}
                        />
                        <FormCheckbox
                            {...register(`treatment_modality_intervention`)}
                            label="Parent Training"
                            value="Parent Training"
                            defaultChecked={isChecked(
                                "Parent Training",
                                noteDetails.note_content
                                    ?.treatment_modality_intervention
                            )}
                        />
                        <FormCheckbox
                            {...register(`treatment_modality_intervention`)}
                            label="Crisis Intervention"
                            value="Crisis Intervention"
                            defaultChecked={isChecked(
                                "Crisis Intervention",
                                noteDetails.note_content
                                    ?.treatment_modality_intervention
                            )}
                        />
                        <FormCheckbox
                            {...register(`treatment_modality_intervention`)}
                            label="Other"
                            value="Other"
                            defaultChecked={isChecked(
                                "Other",
                                noteDetails.note_content
                                    ?.treatment_modality_intervention
                            )}
                        />
                    </div>
                    {errors.treatment_modality_intervention ? (
                        <div className="note-error">
                            <p>Treatment modality/intervention is required</p>
                        </div>
                    ) : null}
                    <div className="fg">
                        <Input
                            {...register(
                                `other_treatment_modality_intervention`
                            )}
                            label={
                                FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.other_treatment_modality_intervention
                            }
                            defaultValue={
                                noteDetails.note_content
                                    ?.other_treatment_modality_intervention
                            }
                            isOptional
                        />
                    </div>
                    <p className="note-label">Treatment goals</p>
                    <div className="flex flex-col gap-y-[--spacing-form-items] mb-[--spacing-form-items]">
                        <Input
                            {...register(`treatment_goal_one`)}
                            label={
                                FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.treatment_goal_one
                            }
                            defaultValue={
                                noteDetails.note_content?.treatment_goal_one
                            }
                            isRequired
                            hasError={!!errors.treatment_goal_one}
                            errorText={errors.treatment_goal_one?.message}
                        />
                        <Input
                            {...register(`treatment_goal_two`)}
                            label={
                                FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.treatment_goal_two
                            }
                            defaultValue={
                                noteDetails.note_content?.treatment_goal_two
                            }
                            hasError={!!errors.treatment_goal_two}
                            errorText={errors.treatment_goal_two?.message}
                            isRequired
                        />
                        <Input
                            {...register(`treatment_goal_three`)}
                            label={
                                FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.treatment_goal_three
                            }
                            defaultValue={
                                noteDetails.note_content?.treatment_goal_three
                            }
                            isOptional
                        />
                    </div>
                    <h3 className="note-label-req">
                        {
                            FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.frequency_of_sessions
                        }
                    </h3>
                    <div className="note-radios">
                        <RadioInput
                            {...register("frequency_of_sessions")}
                            label="Weekly"
                            value="Weekly"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.frequency_of_sessions === "Weekly"
                            }
                        />
                        <RadioInput
                            {...register("frequency_of_sessions")}
                            label="Bi-Weekly"
                            value="Bi-Weekly"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.frequency_of_sessions === "Bi-Weekly"
                            }
                        />
                        <RadioInput
                            {...register("frequency_of_sessions")}
                            label="Monthly"
                            value="Monthly"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.frequency_of_sessions === "Monthly"
                            }
                        />
                        <RadioInput
                            {...register("frequency_of_sessions")}
                            label="Other"
                            value="Other"
                            defaultChecked={
                                noteDetails.note_content
                                    ?.frequency_of_sessions === "Other"
                            }
                        />
                    </div>
                    {errors.frequency_of_sessions ? (
                        <div className="note-error">
                            <p>This field is required</p>
                        </div>
                    ) : null}
                    {watch("frequency_of_sessions") === "Other" && (
                        <div className="fg">
                            <Input
                                {...register("other_frequency_of_sessions")}
                                label={
                                    FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.other_frequency_of_sessions
                                }
                                hasError={!!errors.other_frequency_of_sessions}
                                errorText={
                                    errors.other_frequency_of_sessions?.message
                                }
                                defaultValue={
                                    noteDetails.note_content
                                        ?.other_frequency_of_sessions
                                }
                            />
                        </div>
                    )}
                    <div className="fg">
                        <Controller
                            name="date_of_next_session"
                            control={control}
                            render={({ field }) => (
                                <DatePicker
                                    label={
                                        FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.date_of_next_session
                                    }
                                    onChange={(date) => {
                                        field.onChange(date);
                                        setLocalDateOfNextSession(date);
                                    }}
                                    selected={localDateOfNextSession}
                                    value={
                                        field.value
                                            ? formatDate(field.value, true)
                                            : undefined
                                    }
                                    hasError={!!errors.date_of_next_session}
                                    errorText={
                                        errors.date_of_next_session?.type ===
                                        "typeError"
                                            ? "Invalid date value"
                                            : errors.date_of_next_session
                                                  ?.message
                                    }
                                    shouldCloseOnSelect
                                    isRequired
                                    minDate={
                                        noteDetails.date_of_service
                                            ? addDays(
                                                  parseISO(
                                                      noteDetails.date_of_service
                                                  ),
                                                  1
                                              )
                                            : undefined
                                    }
                                />
                            )}
                        />
                    </div>
                    <h3 className="note-label-req">
                        {
                            FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.subjective_report
                        }
                    </h3>
                    <p className="note-label">
                        Please make sure to only include what the client has
                        reported. This should only tie back to what the client
                        reported in the session, re; symptoms, mood/affect,
                        ideations, interpersonal conflicts, etc. Do not include
                        your assessment or interpretation of the clients
                        symptoms here.
                    </p>
                    <div className="fg">
                        <TextArea
                            {...register(`subjective_report`)}
                            defaultValue={
                                noteDetails.note_content?.subjective_report
                            }
                            hasError={!!errors.subjective_report}
                            errorText={errors.subjective_report?.message}
                        />
                    </div>
                    <h3 className="note-label-req">
                        {
                            FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.summary
                        }
                    </h3>
                    <div className="fg">
                        <TextArea
                            {...register(`summary`)}
                            defaultValue={noteDetails.note_content?.summary}
                            hasError={!!errors.summary}
                            errorText={errors.summary?.message}
                        />
                    </div>
                    <h3 className="note-label-req">
                        {
                            FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.prognosis
                        }
                    </h3>
                    <p className="note-label">
                        Please write at least 1-2 sentences about the likely
                        outcome/development of the symptoms/challenges
                    </p>
                    <div className="fg">
                        <TextArea
                            {...register("prognosis")}
                            defaultValue={noteDetails.note_content?.prognosis}
                        />
                    </div>
                    <h3 className="note-label note-label-req">
                        {
                            FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.medical_necessity_rationale
                        }
                    </h3>
                    <div className="note-checkboxes">
                        <Controller
                            name="medical_necessity_rationale"
                            control={control}
                            render={({ field }) => {
                                const checkboxValue =
                                    "Symptoms result in clinically significant distress and/or functional impairment warranting outpatient psychotherapy at the recommended frequency";
                                return (
                                    <FormCheckbox
                                        label={checkboxValue}
                                        value={checkboxValue}
                                        checked={
                                            field.value?.includes(
                                                checkboxValue
                                            ) ?? false
                                        }
                                        onChange={(e) =>
                                            field.onChange(
                                                e.target.checked
                                                    ? [checkboxValue]
                                                    : null
                                            )
                                        }
                                    />
                                );
                            }}
                        />
                    </div>
                    {errors.medical_necessity_rationale ? (
                        <div className="note-error">
                            <p>Medical necessity rationale is required</p>
                        </div>
                    ) : null}

                    <h3 className="note-label note-label-req">
                        {
                            FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.risk_review_supervision_plan
                        }
                    </h3>
                    <div className="note-checkboxes">
                        <FormCheckbox
                            {...register("risk_review_supervision_plan")}
                            label="Risk reviewed by supervising clinician"
                            value="Risk reviewed by supervising clinician"
                            defaultChecked={isCheckboxChecked(
                                "Risk reviewed by supervising clinician",
                                "risk_review_supervision_plan"
                            )}
                        />
                        <FormCheckbox
                            {...register("risk_review_supervision_plan")}
                            label="Appropriate level of care determined"
                            value="Appropriate level of care determined"
                            defaultChecked={isCheckboxChecked(
                                "Appropriate level of care determined",
                                "risk_review_supervision_plan"
                            )}
                        />
                        <FormCheckbox
                            {...register("risk_review_supervision_plan")}
                            label="Risk/safety concerns addressed as clinically indicated"
                            value="Risk/safety concerns addressed as clinically indicated"
                            defaultChecked={
                                isCheckboxChecked(
                                    "Risk/safety concerns addressed as clinically indicated",
                                    "risk_review_supervision_plan"
                                ) ||
                                isCheckboxChecked(
                                    "Safety planning addressed as clinically indicated",
                                    "risk_review_supervision_plan"
                                )
                            }
                        />
                        <FormCheckbox
                            {...register("risk_review_supervision_plan")}
                            label="Formal safety plan completed/updated"
                            value="Formal safety plan completed/updated"
                            defaultChecked={isCheckboxChecked(
                                "Formal safety plan completed/updated",
                                "risk_review_supervision_plan"
                            )}
                        />
                    </div>
                    {shouldShowFormalSafetyPlan ? (
                        <div className="fg">
                            <TextArea
                                {...register("formal_safety_plan_details")}
                                label={
                                    FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.formal_safety_plan_details
                                }
                                defaultValue={
                                    noteDetails.note_content
                                        ?.formal_safety_plan_details
                                }
                            />
                        </div>
                    ) : null}
                    <div className="note-checkboxes">
                        <FormCheckbox
                            {...register("risk_review_supervision_plan")}
                            label="Higher level of care considered/recommended"
                            value="Higher level of care considered/recommended"
                            defaultChecked={isCheckboxChecked(
                                "Higher level of care considered/recommended",
                                "risk_review_supervision_plan"
                            )}
                        />
                    </div>
                    {shouldShowHigherLevelOfCare ? (
                        <div className="fg">
                            <TextArea
                                {...register("higher_level_of_care_details")}
                                label={
                                    FAMILY_INITIAL_DIAGNOSTIC_EVALUATION_NOTE_DETAILS.higher_level_of_care_details
                                }
                                defaultValue={
                                    noteDetails.note_content
                                        ?.higher_level_of_care_details
                                }
                            />
                        </div>
                    ) : null}
                    {errors.risk_review_supervision_plan ? (
                        <div className="note-error">
                            <p>Risk review supervision plan is required</p>
                        </div>
                    ) : null}
                </section>
                <div className="fg-info fg-line">
                    <p>Sign note here</p>
                    <div className="fg">
                        <Input
                            {...register("signature")}
                            hasError={!!errors.signature}
                            errorText={errors.signature?.message}
                            label="Provider's Initials"
                            autoComplete="off"
                            isRequired
                        />
                    </div>
                </div>
                <div>
                    <EditNoteFooter
                        onSaveButtonClick={() => {
                            isSigning.current = false;
                            handleSubmit(handleEditDraftNote)();
                        }}
                        noteDOS={noteDetails.date_of_service}
                        onSubmitButtonClick={() => {
                            isSigning.current = true;
                        }}
                    />
                </div>
            </form>
        </>
    );
}
